import {
  Client,
  Subscription,
  Alert,
  AuditLog,
  PaginatedResponse,
  DashboardData,
  CreateClientRequest,
  UpdateClientRequest,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  UpdateAlertRequest,
  ClientStatus,
  PaymentStatus,
  SubscriptionStatus,
  AlertType,
  AlertSeverity,
  AlertStatus,
  EntityType,
  AuditAction,
  ClientUsersResponse,
  DetailedUser,
  TenantGroupsResponse,
  GroupMember,
  PasswordResetResponse,
  ActionResponse,
  OffboardUserRequest,
  OffboardingResponse,
  OffboardingRecord,
  ReportDefinition,
  ReportExecution,
  ReportType,
  ReportExecutionStatus,
  CreateReportDefinitionRequest,
  UpdateReportDefinitionRequest,
} from '../types';

// In embed mode, call Function App directly to bypass SWA proxy (avoids header/cookie issues)
// In normal mode, use SWA proxy which handles auth
const FUNCTION_APP_URL = 'https://pax8lic-api.azurewebsites.net/api';

function getApiBase(): string {
  // Check if we're in embed mode (iframe)
  try {
    if (window.self !== window.top) {
      return FUNCTION_APP_URL;
    }
  } catch {
    // Cross-origin iframe - use direct URL
    return FUNCTION_APP_URL;
  }
  // SWA preview environments don't have linked backend — call Function App directly
  if (window.location.hostname.includes('-1.') || window.location.hostname.includes('-staging.')) {
    return FUNCTION_APP_URL;
  }
  return '/api';
}

let getAccessToken: (() => Promise<string>) | null = null;

// Cache the SWA client principal for direct API calls (preview/staging)
let cachedClientPrincipal: string | null = null;

async function getSwaClientPrincipal(): Promise<string | null> {
  if (cachedClientPrincipal) return cachedClientPrincipal;
  try {
    const res = await fetch('/.auth/me');
    const data = await res.json();
    if (data.clientPrincipal) {
      // Slim the principal before base64-encoding. The full clientPrincipal
      // from SWA includes every claim from the AAD token — users in many
      // groups (some staff carry 70+ transitive Entra group memberships)
      // produce a header that blows past the Azure Function App 8KB header
      // limit, which truncates it and makes the middleware fall through to
      // 401 "Not authenticated". The middleware only reads userId +
      // userDetails; empty groups is handled by the ALLOWED_GROUP_ID check's
      // "no groups in token → skip group check" fallback.
      const p = data.clientPrincipal as {
        identityProvider?: string;
        userId?: string;
        userDetails?: string;
        userRoles?: string[];
      };
      const slim = {
        identityProvider: p.identityProvider,
        userId: p.userId,
        userDetails: p.userDetails,
        userRoles: p.userRoles,
        claims: [] as unknown[],
      };
      cachedClientPrincipal = btoa(JSON.stringify(slim));
      return cachedClientPrincipal;
    }
  } catch { /* ignore */ }
  return null;
}

function isDirectApiMode(): boolean {
  return getApiBase() === FUNCTION_APP_URL && !isEmbedMode();
}

// Debug logging helper - dynamically imports to avoid circular dependencies
async function logDebug(
  type: 'info' | 'success' | 'warning' | 'error' | 'request' | 'response',
  source: 'Graph' | 'Pax8' | 'Sync' | 'Cosmos' | 'System' | 'Auth',
  message: string,
  details?: string
) {
  try {
    const { addDebugLog } = await import('../components/DebugPanel/FloatingDebugPanel');
    addDebugLog({ type, source, message, details });
  } catch {
    // Debug panel not available, just log to console
    console.log(`[${source}] ${message}`, details || '');
  }
}

// Store HUD user email received via postMessage
let hudUserEmail: string | null = null;

// Detect if running in an iframe (embed mode)
function isEmbedMode(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin, we're in an iframe
    return true;
  }
}

// Initialize HUD postMessage listener for receiving authenticated user info
function initHudAuthListener(): void {
  if (!isEmbedMode()) return;

  window.addEventListener('message', (event) => {
    // Only accept messages from trusted HUD origins
    const trustedOrigins = [
      'https://cloudops.umetech.net',
      'https://calm-coast-0045b821e.3.azurestaticapps.net',
      'http://localhost:5173'
    ];

    if (!trustedOrigins.includes(event.origin)) return;

    if (event.data?.type === 'HUD_USER_AUTH') {
      hudUserEmail = event.data.email || null;
      console.log('[Pax8 Licensing] Received HUD user auth:', hudUserEmail);
    }
  });

  console.log('[Pax8 Licensing] HUD auth listener initialized');
}

// Initialize the listener when this module loads
initHudAuthListener();

export function setTokenProvider(provider: () => Promise<string>) {
  getAccessToken = provider;
}

// Direct call to Function App, bypassing the SWA proxy (no 45s timeout).
// Use for runbook-backed endpoints that routinely take > 45 seconds.
async function fetchDirect(url: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // In HUD iframe embed mode, SWA cookies don't reliably flow to a direct Function App call;
  // signal embed auth the same way fetchWithAuth does so the middleware accepts the request.
  if (isEmbedMode()) {
    headers['X-Embed-Mode'] = 'true';
    if (hudUserEmail) {
      headers['X-Embed-User-Email'] = hudUserEmail;
    }
  }

  // Forward SWA client principal so the Function App can authenticate the user.
  // NOTE: We send it as `x-swa-client-principal`, NOT `x-ms-client-principal`.
  // Azure App Service strips all `x-ms-*` headers at ingress (they're
  // reserved for platform internals), so a browser-set `x-ms-client-principal`
  // never reaches the function code. The middleware knows to read this name.
  const principal = await getSwaClientPrincipal();
  if (principal) {
    headers['x-swa-client-principal'] = principal;
  }

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch { /* ignore */ }
  }

  const response = await fetch(`${FUNCTION_APP_URL}${url}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorData: { error?: string } = { error: `HTTP error ${response.status}` };
    try { errorData = await response.json(); } catch { /* ignore */ }
    throw new Error(errorData.error || `HTTP error ${response.status}`);
  }

  return response;
}

async function fetchWithAuth(url: string, options: RequestInit = {}, meta: { allowHtml?: boolean } = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // In embed mode (iframe), add headers so backend knows to use embed auth
  // Security: CSP frame-ancestors restricts who can embed, backend validates
  if (isEmbedMode()) {
    headers['X-Embed-Mode'] = 'true';
    // If HUD sent us the authenticated user's email via postMessage, include it
    if (hudUserEmail) {
      headers['X-Embed-User-Email'] = hudUserEmail;
    }
  }

  // For preview/staging: forward SWA client principal to Function App
  if (isDirectApiMode()) {
    const principal = await getSwaClientPrincipal();
    if (principal) {
      headers['x-ms-client-principal'] = principal;
    }
  }

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to get access token:', error);
      await logDebug('error', 'Auth', 'Failed to get access token', error instanceof Error ? error.message : String(error));
    }
  }

  const apiBase = getApiBase();
  const response = await fetch(`${apiBase}${url}`, {
    ...options,
    headers,
    credentials: 'include', // Include cookies for SWA auth
  });

  // Don't try to redirect from here - let the auth system handle it
  // Just throw meaningful errors that the UI can display

  if (!response.ok) {
    // Try to parse error response - capture full details for debugging
    let errorData: { error?: string; details?: string; name?: string; timestamp?: string } = { error: `HTTP error ${response.status}` };
    try {
      errorData = await response.json();
    } catch {
      // Response wasn't JSON, try to get text
      try {
        const text = await response.text();
        errorData = { error: `HTTP ${response.status}`, details: text.substring(0, 500) };
      } catch {
        // Can't read body at all
      }
    }

    // Log full error details to debug panel
    const errorDetails = [
      `Status: ${response.status}`,
      `Error: ${errorData.error || 'Unknown'}`,
      errorData.name ? `Type: ${errorData.name}` : null,
      errorData.details ? `Details:\n${errorData.details}` : null,
      errorData.timestamp ? `Time: ${errorData.timestamp}` : null
    ].filter(Boolean).join('\n');

    await logDebug('error', 'Auth', `HTTP ${response.status} on ${url}`, errorDetails);

    // For auth errors, show the actual backend error message
    if (response.status === 401 || response.status === 403) {
      throw new Error(errorData.error || 'Authentication required. Please refresh the page or sign in again.');
    }

    throw new Error(errorData.error || `HTTP error ${response.status}`);
  }

  // Check if we got HTML instead of JSON (usually an SWA auth-redirect page).
  // Endpoints that legitimately return HTML (e.g. the HIPAA report) pass
  // meta.allowHtml so a real HTML body isn't misread as an auth failure.
  const contentType = response.headers.get('content-type');
  if (!meta.allowHtml && contentType && contentType.includes('text/html')) {
    await logDebug('error', 'Auth', `Got HTML instead of JSON on ${url}`, 'Authentication redirect detected');
    throw new Error('Authentication required. Please refresh the page or sign in again.');
  }

  return response;
}

// Note: Direct Function App calls removed - SWA auth uses cookies which don't work cross-origin
// All API calls now go through SWA proxy with proper cookie auth

// Dashboard
export async function getDashboardStats(): Promise<DashboardData> {
  const response = await fetchWithAuth('/dashboard/stats');
  return response.json();
}

// Clients
export interface ClientFilters {
  status?: ClientStatus;
  paymentStatus?: PaymentStatus;
  search?: string;
  page?: number;
  pageSize?: number;
  includeSubscriptions?: boolean;  // Include subscriptions for license issue display
}

export async function getClients(filters: ClientFilters = {}): Promise<PaginatedResponse<Client>> {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
  if (filters.includeSubscriptions) params.append('includeSubscriptions', 'true');

  const response = await fetchWithAuth(`/clients?${params.toString()}`);
  return response.json();
}

export async function getClient(id: string): Promise<Client> {
  const response = await fetchWithAuth(`/clients/${id}`);
  return response.json();
}

export async function createClient(data: CreateClientRequest): Promise<Client> {
  const response = await fetchWithAuth('/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateClient(id: string, data: UpdateClientRequest): Promise<Client> {
  const response = await fetchWithAuth(`/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Subscriptions
export interface SubscriptionFilters {
  clientId?: string;
  status?: SubscriptionStatus;
  productName?: string;
  renewalDateFrom?: string;
  renewalDateTo?: string;
  atRiskOnly?: boolean;
  autoRenew?: boolean;
  search?: string;
  sortBy?: string;
  page?: number;
  pageSize?: number;
}

export async function getSubscriptions(filters: SubscriptionFilters = {}): Promise<PaginatedResponse<Subscription>> {
  const params = new URLSearchParams();
  if (filters.clientId) params.append('clientId', filters.clientId);
  if (filters.status) params.append('status', filters.status);
  if (filters.productName) params.append('productName', filters.productName);
  if (filters.renewalDateFrom) params.append('renewalDateFrom', filters.renewalDateFrom);
  if (filters.renewalDateTo) params.append('renewalDateTo', filters.renewalDateTo);
  if (filters.atRiskOnly) params.append('atRiskOnly', 'true');
  if (filters.autoRenew !== undefined) params.append('autoRenew', filters.autoRenew.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

  const response = await fetchWithAuth(`/subscriptions?${params.toString()}`);
  return response.json();
}

export interface AvailableLicense {
  msSkuId: string;
  msSkuPartNumber: string;
  productName: string;
  totalLicenses: number;
  assignedLicenses: number;
  availableLicenses: number;
}

export async function getAvailableLicenses(clientId: string): Promise<{ items: AvailableLicense[] }> {
  const response = await fetchWithAuth(`/clients/${clientId}/available-licenses`);
  return response.json();
}

export async function getSubscription(id: string): Promise<Subscription> {
  const response = await fetchWithAuth(`/subscriptions/${id}`);
  return response.json();
}

export async function createSubscription(data: CreateSubscriptionRequest): Promise<Subscription> {
  const response = await fetchWithAuth('/subscriptions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateSubscription(id: string, data: UpdateSubscriptionRequest): Promise<Subscription> {
  const response = await fetchWithAuth(`/subscriptions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Update subscription quantity via Pax8 API (Pax8 subscriptions only)
export interface UpdateQuantityResponse {
  success: boolean;
  message: string;
  subscription?: unknown;
  error?: string;
  details?: string;
}

export async function updatePax8SubscriptionQuantity(
  subscriptionId: string,
  quantity: number
): Promise<UpdateQuantityResponse> {
  const response = await fetchWithAuth(`/subscriptions/${subscriptionId}/quantity`, {
    method: 'PATCH',
    body: JSON.stringify({ quantity }),
  });
  return response.json();
}

// Alerts
export interface AlertFilters {
  clientId?: string;
  alertType?: AlertType;
  severity?: AlertSeverity;
  status?: AlertStatus;
  page?: number;
  pageSize?: number;
}

export async function getAlerts(filters: AlertFilters = {}): Promise<PaginatedResponse<Alert>> {
  const params = new URLSearchParams();
  if (filters.clientId) params.append('clientId', filters.clientId);
  if (filters.alertType) params.append('alertType', filters.alertType);
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

  const response = await fetchWithAuth(`/alerts?${params.toString()}`);
  return response.json();
}

export async function updateAlert(id: string, data: UpdateAlertRequest): Promise<Alert> {
  const response = await fetchWithAuth(`/alerts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Reports
export async function getRenewalReport(fromDate: string, toDate: string): Promise<{ items: Subscription[]; totalCount: number }> {
  const params = new URLSearchParams({ fromDate, toDate });
  const response = await fetchWithAuth(`/reports/renewals?${params.toString()}`);
  return response.json();
}

export async function getUtilizationReport(maxUtilization?: number): Promise<{ items: (Subscription & { utilizationPercent: number })[]; totalCount: number }> {
  const params = new URLSearchParams();
  if (maxUtilization !== undefined) params.append('maxUtilization', maxUtilization.toString());
  const response = await fetchWithAuth(`/reports/utilization?${params.toString()}`);
  return response.json();
}

// Sync
export async function triggerPax8Sync(): Promise<{ message: string; clientsProcessed: number; subscriptionsProcessed: number }> {
  const response = await fetchWithAuth('/sync/pax8', { method: 'POST' });
  return response.json();
}

export async function triggerGDAPSync(): Promise<{ message: string; clientsProcessed: number; subscriptionsProcessed: number }> {
  const response = await fetchWithAuth('/sync/gdap', { method: 'POST' });
  return response.json();
}

export interface FullSyncResponse {
  message: string;
  summary: {
    pax8: { clients: number; subscriptions: number };
    tenantIdsPopulated: number;
  };
  errors: number;
  errorDetails: string[];
}

export async function triggerFullSync(): Promise<FullSyncResponse> {
  const response = await fetchWithAuth('/sync/full', { method: 'POST' });
  return response.json();
}

export async function syncTenantIds(): Promise<{
  message: string;
  summary: {
    totalClients: number;
    clientsUpdated: number;
    clientsSkipped: string;
    clientsNotMatched: string;
  };
  partnerCenterCustomers: number;
  matchedClients: { name: string; tenantId: string }[];
  unmatchedClients: string[];
}> {
  const response = await fetchWithAuth('/sync/tenant-ids', { method: 'POST' });
  return response.json();
}

export interface TAMSyncResponse {
  message: string;
  source: string;
  summary: {
    totalClients: number;
    tamMappings: number;
    clientsUpdated: number;
    clientsSkipped: string;
    clientsNotMatched: string;
  };
  matchedClients: { name: string; tenantId: string; domain: string; matchType: string }[];
  unmatchedClients: string[];
  matchBreakdown: {
    exact: number;
    clean: number;
    partial: number;
  };
  note?: string;
}

export async function syncTenantIdsTAM(): Promise<TAMSyncResponse> {
  const response = await fetchWithAuth('/sync/tam-tenant-ids', { method: 'POST' });
  return response.json();
}

// Partner Center OAuth - for syncing tenant IDs with interactive login
export async function getPartnerCenterAuthUrl(): Promise<{ authUrl: string; state: string }> {
  const response = await fetchWithAuth('/partner-center/auth-url');
  return response.json();
}

export interface SyncClientResponse {
  message: string;
  clientName: string;
  lastSyncDate: string;
  subscriptionsProcessed: number;
  subscriptionsAdded: number;
  subscriptionsUpdated: number;
  errorsEncountered: number;
  errorDetails: string[];
}

export async function syncClient(clientId: string, type: 'pax8' | 'ms' = 'pax8'): Promise<SyncClientResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/sync?type=${type}`, { method: 'POST' });
  return response.json();
}

// Test Partner Center API (temporary for debugging)
export async function testPartnerCenter(tenantId: string): Promise<Record<string, unknown>> {
  const response = await fetchWithAuth(`/test-partner-center?tenantId=${tenantId}`);
  return response.json();
}

export interface SyncNewClientsResponse {
  message: string;
  newClientsAdded: number;
  newClientNames: string[];
  subscriptionsProcessed: number;
  totalPax8Companies: number;
  errorsEncountered: number;
  errorDetails: string[];
}

export async function syncNewClients(): Promise<SyncNewClientsResponse> {
  const response = await fetchWithAuth('/sync/new-clients', { method: 'POST' });
  return response.json();
}

export interface AlertGenerationDiagnostics {
  subscriptionsWithRenewalDate: number;
  subscriptionsWithoutRenewalDate: number;
  flaggedPaymentClients: number;
  atRiskClients: number;
  subscriptionsWithUtilizationData: number;
  renewalsNext30Days: number;
  renewalsNext60Days: number;
  sampleSubscriptions: Array<{ clientName: string; productName: string; renewalDate: string; status: string }>;
}

export interface GenerateAlertsResponse {
  message: string;
  alertsCreated: number;
  subscriptionsProcessed: number;
  clientsProcessed: number;
  errorsEncountered: number;
  errorDetails: string[];
  diagnostics?: AlertGenerationDiagnostics;
}

export async function generateAlerts(): Promise<GenerateAlertsResponse> {
  const response = await fetchWithAuth('/alerts/generate', { method: 'POST' });
  return response.json();
}

export interface AlertScanStatus {
  lastScanTime: string | null;
  nextScheduledScan: string;
  scanInterval: string;
}

export async function getAlertScanStatus(): Promise<AlertScanStatus> {
  const response = await fetchWithAuth('/alerts/scan-status');
  return response.json();
}

// Audit Logs
export interface AuditLogFilters {
  entityType?: EntityType;
  entityId?: string;
  action?: AuditAction;
  userEmail?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export async function getAuditLogs(filters: AuditLogFilters = {}): Promise<PaginatedResponse<AuditLog>> {
  const params = new URLSearchParams();
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.entityId) params.append('entityId', filters.entityId);
  if (filters.action) params.append('action', filters.action);
  if (filters.userEmail) params.append('userEmail', filters.userEmail);
  if (filters.fromDate) params.append('fromDate', filters.fromDate);
  if (filters.toDate) params.append('toDate', filters.toDate);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

  const response = await fetchWithAuth(`/audit-logs?${params.toString()}`);
  return response.json();
}

// Sync History
export interface SyncHistoryItem {
  id: string;
  syncType: 'pax8' | 'gdap' | 'full' | 'alert-generation' | 'tenant-ids';
  startTime: string;
  endTime: string;
  status: 'success' | 'partial' | 'failed';
  clientsProcessed: number;
  subscriptionsProcessed: number;
  errorsEncountered: number;
  errorDetails: string[];
}

export async function getSyncHistory(limit: number = 50): Promise<{ items: SyncHistoryItem[]; total: number }> {
  const response = await fetchWithAuth(`/sync/history?limit=${limit}`);
  return response.json();
}

// Billing Verification - OAuth flow to check MS billing status
export async function getBillingAuthUrl(clientId: string, tenantId: string): Promise<{ authUrl: string }> {
  const response = await fetchWithAuth(`/GetBillingAuthUrl?clientId=${encodeURIComponent(clientId)}&tenantId=${encodeURIComponent(tenantId)}`);
  return response.json();
}

// Client Users - fetch users from tenant via Graph API
export async function getClientUsers(clientId: string): Promise<ClientUsersResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users`);
  return response.json();
}

// Create new user in tenant
export interface CreateUserRequest {
  displayName: string;
  email: string;          // Full email with domain (e.g., user@company.com)
  password: string;
  usageLocation?: string;
  forceChangePasswordNextSignIn?: boolean;
  licenseSkuId?: string;  // Optional: assign license immediately
}

export interface CreateUserResponse {
  success: boolean;
  user?: {
    id: string;
    displayName: string;
    userPrincipalName: string;
  };
  licenseAssigned?: boolean;
  licenseError?: string;
  message?: string;
  error?: string;
}

export interface DuplicateUserInfo {
  upn: string;
  foundInDeleted: boolean;
  deletedUserId?: string;
  deletedDisplayName?: string;
  foundInActive: boolean;
  activeUserId?: string;
  activeDisplayName?: string;
}

export class DuplicateUserError extends Error {
  duplicateInfo: DuplicateUserInfo;
  constructor(message: string, duplicateInfo: DuplicateUserInfo) {
    super(message);
    this.name = 'DuplicateUserError';
    this.duplicateInfo = duplicateInfo;
  }
}

export async function createUser(clientId: string, userData: CreateUserRequest): Promise<CreateUserResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (isEmbedMode()) {
    headers['X-Embed-Mode'] = 'true';
    if (hudUserEmail) headers['X-Embed-User-Email'] = hudUserEmail;
  }
  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch { /* ignore */ }
  }

  const apiBase = getApiBase();
  const response = await fetch(`${apiBase}/clients/${clientId}/users`, {
    method: 'POST',
    headers,
    body: JSON.stringify(userData),
    credentials: 'include',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // Check for duplicate UPN with enriched info
    if (response.status === 409 && data.duplicateInfo) {
      throw new DuplicateUserError(data.error, data.duplicateInfo);
    }
    throw new Error(data.error || `HTTP error ${response.status}`);
  }

  return response.json();
}

export async function purgeAndCreateUser(clientId: string, data: {
  deletedUserId: string;
  displayName: string;
  email: string;
  password: string;
  usageLocation?: string;
  forceChangePasswordNextSignIn?: boolean;
  licenseSkuId?: string;
}): Promise<CreateUserResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/purge-and-create-user`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// User Detail - fetch detailed user info including roles and groups
export async function getUserDetail(clientId: string, userId: string): Promise<DetailedUser> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}`);
  return response.json();
}

// Tenant Groups - fetch all groups in a tenant
export async function getTenantGroups(clientId: string): Promise<TenantGroupsResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/groups`);
  return response.json();
}

// Group Members - fetch members of a specific group
export async function getGroupMembers(clientId: string, groupId: string): Promise<GroupMember[]> {
  const response = await fetchWithAuth(`/clients/${clientId}/groups/${groupId}/members`);
  return response.json();
}

// Create a new group in a tenant
export async function createGroup(clientId: string, data: {
  displayName: string;
  description?: string;
  groupType: 'security' | 'microsoft365';
  mailNickname?: string;
}): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/groups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Delete a group from a tenant
export async function deleteGroup(clientId: string, groupId: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/groups/${groupId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// Create a distribution list via Exchange Online runbook
export interface CreateDistributionListResponse {
  success: boolean;
  groupCreated: boolean;
  group?: {
    displayName: string;
    email: string;
    alias: string;
  };
  message?: string;
  error?: string;
}

export async function createDistributionList(
  clientId: string,
  data: { displayName: string; emailAddress: string; alias?: string }
): Promise<CreateDistributionListResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/distribution-lists`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Delete a distribution list via Exchange Online runbook
export interface DeleteDistributionListResponse {
  success: boolean;
  groupDeleted?: boolean;
  message?: string;
  error?: string;
}

export async function deleteDistributionList(
  clientId: string,
  groupEmail: string
): Promise<DeleteDistributionListResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/distribution-lists/${encodeURIComponent(groupEmail)}`, {
    method: 'DELETE',
  });
  return response.json();
}

// Assign license to user
export async function assignUserLicense(clientId: string, userId: string, skuId: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/licenses`, {
    method: 'POST',
    body: JSON.stringify({ skuId }),
  });
  return response.json();
}

// Remove license from user
export async function removeUserLicense(clientId: string, userId: string, skuId: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/licenses/${skuId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// Add user to group
export async function addUserToGroup(clientId: string, userId: string, groupId: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/groups`, {
    method: 'POST',
    body: JSON.stringify({ groupId }),
  });
  return response.json();
}

// Remove user from group
export async function removeUserFromGroup(clientId: string, userId: string, groupId: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/groups/${groupId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// Get available directory roles for a tenant
export async function getDirectoryRoles(clientId: string): Promise<{ roles: { id: string; displayName: string; description: string }[] }> {
  const response = await fetchWithAuth(`/clients/${clientId}/directory-roles`);
  return response.json();
}

// Assign a directory role to a user
export async function assignUserRole(clientId: string, userId: string, roleId: string, roleName?: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/directory-roles`, {
    method: 'POST',
    body: JSON.stringify({ roleId, roleName, action: 'add' }),
  });
  return response.json();
}

// Remove a directory role from a user
export async function removeUserRole(clientId: string, userId: string, roleId: string, roleName?: string): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/directory-roles`, {
    method: 'POST',
    body: JSON.stringify({ roleId, roleName, action: 'remove' }),
  });
  return response.json();
}

// Reset user password
export async function resetUserPassword(
  clientId: string,
  userId: string,
  options?: { newPassword?: string; forceChangeOnNextSignIn?: boolean }
): Promise<PasswordResetResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
  return response.json();
}

// Send password reset via email/SMS (split-channel if RingCentral connected)
export interface SendPasswordResetResponse {
  success: boolean;
  error?: string;
  message?: string;
  deliveryMethod?: 'split' | 'email';
  smsError?: string;
  user?: {
    id: string;
    displayName: string;
    userPrincipalName: string;
  };
}

export async function sendPasswordReset(
  clientId: string,
  userId: string,
  recipientEmail: string,
  recipientPhoneNumber?: string
): Promise<SendPasswordResetResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/send-password-reset`, {
    method: 'POST',
    body: JSON.stringify({ recipientEmail, recipientPhoneNumber }),
  });
  return response.json();
}

// Change user email / UPN domain
export interface UpdateUserUPNResponse {
  success?: boolean;
  message?: string;
  oldUserPrincipalName?: string;
  newUserPrincipalName?: string;
  displayName?: string;
  error?: string;
}

export async function updateUserUPN(clientId: string, userId: string, newUserPrincipalName: string): Promise<UpdateUserUPNResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/upn`, {
    method: 'PATCH',
    body: JSON.stringify({ newUserPrincipalName }),
  });
  return response.json();
}

// Update user profile properties
export interface UpdateUserPropertiesRequest {
  displayName?: string;
  jobTitle?: string;
  department?: string;
  mobilePhone?: string;
  officeLocation?: string;
}

export interface UpdateUserPropertiesResponse {
  success: boolean;
  message?: string;
  error?: string;
  updatedFields?: string[];
}

export async function updateUserProperties(
  clientId: string,
  userId: string,
  properties: UpdateUserPropertiesRequest
): Promise<UpdateUserPropertiesResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/properties`, {
    method: 'PATCH',
    body: JSON.stringify(properties),
  });
  return response.json();
}

// Delete user permanently
export interface DeleteUserResponse {
  success: boolean;
  message?: string;
  error?: string;
  deletedUser?: {
    id: string;
    displayName: string;
    userPrincipalName: string;
  };
}

export async function deleteUser(clientId: string, userId: string): Promise<DeleteUserResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/delete`, {
    method: 'DELETE',
  });
  return response.json();
}

// Enable or disable user account
export async function setUserEnabled(clientId: string, userId: string, enabled: boolean): Promise<ActionResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/enabled`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
  return response.json();
}

// Quick Setup user - one-click enable, license, password reset, send credentials
export interface QuickSetupStep {
  name: string;
  status: 'success' | 'skipped' | 'failed';
  message: string;
  error?: string;
}

export interface QuickSetupResponse {
  success: boolean;
  steps: QuickSetupStep[];
  temporaryPassword?: string;
}

export interface QuickSetupRequest {
  skuId?: string;
  sendCredentials?: boolean;
  recipientEmail?: string;
  recipientPhone?: string;
}

export async function quickSetupUser(
  clientId: string,
  userId: string,
  request: QuickSetupRequest
): Promise<QuickSetupResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/quick-setup`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// Offboard user - execute full offboarding workflow
export async function offboardUser(
  clientId: string,
  userId: string,
  request: OffboardUserRequest
): Promise<OffboardingResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/offboard`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// Get offboarding status
export async function getOffboardingStatus(offboardingId: string): Promise<OffboardingRecord> {
  const response = await fetchWithAuth(`/offboarding/${offboardingId}`);
  return response.json();
}

// Check Exchange operation status for an offboarding record (polls runbook job)
export async function checkOffboardExchangeStatus(offboardingId: string): Promise<OffboardingRecord & { exchangeStatus: string; exchangeError?: string }> {
  const response = await fetchWithAuth(`/offboarding/${offboardingId}/check-exchange`, {
    method: 'POST',
  });
  return response.json();
}

// ============================================
// Scheduled Reports API
// ============================================

export interface ReportDefinitionFilters {
  clientId?: string;
  reportType?: ReportType;
  enabled?: boolean;
  page?: number;
  pageSize?: number;
}

export async function getReportDefinitions(filters: ReportDefinitionFilters = {}): Promise<PaginatedResponse<ReportDefinition>> {
  const params = new URLSearchParams();
  if (filters.clientId) params.append('clientId', filters.clientId);
  if (filters.reportType) params.append('reportType', filters.reportType);
  if (filters.enabled !== undefined) params.append('enabled', filters.enabled.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

  const response = await fetchWithAuth(`/reports/definitions?${params.toString()}`);
  return response.json();
}

export async function getReportDefinition(id: string): Promise<ReportDefinition> {
  const response = await fetchWithAuth(`/reports/definitions/${id}`);
  return response.json();
}

export async function createReportDefinition(data: CreateReportDefinitionRequest): Promise<ReportDefinition> {
  const response = await fetchWithAuth('/reports/definitions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateReportDefinition(id: string, data: UpdateReportDefinitionRequest): Promise<ReportDefinition> {
  const response = await fetchWithAuth(`/reports/definitions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function deleteReportDefinition(id: string): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth(`/reports/definitions/${id}`, {
    method: 'DELETE',
  });
  return response.json();
}

export interface ReportExecutionFilters {
  definitionId?: string;
  clientId?: string;
  reportType?: ReportType;
  status?: ReportExecutionStatus;
  triggeredBy?: 'schedule' | 'manual';
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export async function getReportExecutions(filters: ReportExecutionFilters = {}): Promise<PaginatedResponse<ReportExecution>> {
  const params = new URLSearchParams();
  if (filters.definitionId) params.append('definitionId', filters.definitionId);
  if (filters.clientId) params.append('clientId', filters.clientId);
  if (filters.reportType) params.append('reportType', filters.reportType);
  if (filters.status) params.append('status', filters.status);
  if (filters.triggeredBy) params.append('triggeredBy', filters.triggeredBy);
  if (filters.fromDate) params.append('fromDate', filters.fromDate);
  if (filters.toDate) params.append('toDate', filters.toDate);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

  const response = await fetchWithAuth(`/reports/executions?${params.toString()}`);
  return response.json();
}

export async function getReportExecution(id: string): Promise<ReportExecution> {
  const response = await fetchWithAuth(`/reports/executions/${id}`);
  return response.json();
}

export async function deleteReportExecutions(ids: string[]): Promise<{ success: boolean; deletedCount: number }> {
  const response = await fetchWithAuth('/reports/executions/delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  return response.json();
}

export interface RunReportResponse {
  success: boolean;
  executionId: string;
  message?: string;
  error?: string;
  stats?: Record<string, string | number | boolean>;
}

export async function runReport(definitionId: string, triggeredByUser?: string): Promise<RunReportResponse> {
  const response = await fetchWithAuth(`/reports/definitions/${definitionId}/run`, {
    method: 'POST',
    body: JSON.stringify({ triggeredByUser }),
  });
  return response.json();
}

// Audit logging
export async function logLogin(): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth('/audit/login', {
    method: 'POST',
    body: JSON.stringify({
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });
  return response.json();
}

// ============================================
// Consent Status API
// ============================================

export interface ConsentStatusResult {
  tenantId: string;
  clientName: string;
  appConsent: {
    granted: boolean;
    servicePrincipalId?: string;
    error?: string;
  };
  graphCliConsent: {
    granted: boolean;
    servicePrincipalId?: string;
    error?: string;
  };
  exchangeRole: {
    assigned: boolean;
    error?: string;
  };
  passwordResetPermission: {
    granted: boolean;
    error?: string;
  };
  tapPolicyPermission: {
    granted: boolean;
    error?: string;
  };
  groupMemberPermission: {
    granted: boolean;
    error?: string;
  };
  oversiteConsent: {
    granted: boolean;
    servicePrincipalId?: string;
    error?: string;
  };
  allConsentsGranted: boolean;
  consentUrls: {
    appConsent: string;
    graphCliConsent: string;
    oversiteConsent: string;
  };
  instructions: string[];
  message?: string;
}

export async function getConsentStatus(clientId: string): Promise<ConsentStatusResult> {
  const response = await fetchWithAuth(`/clients/${clientId}/consent-status`);
  return response.json();
}

export interface AssignExchangeRoleResult {
  success: boolean;
  message: string;
  alreadyAssigned?: boolean;
  servicePrincipalId?: string;
  error?: string;
  details?: string;
}

export async function assignExchangeRole(clientId: string): Promise<AssignExchangeRoleResult> {
  const response = await fetchWithAuth(`/clients/${clientId}/assign-exchange-role`, {
    method: 'POST',
  });
  return response.json();
}

export interface ResetAppConsentResult {
  success: boolean;
  message: string;
  wasDeleted: boolean;
  error?: string;
}

export async function resetAppConsent(clientId: string): Promise<ResetAppConsentResult> {
  const response = await fetchWithAuth(`/clients/${clientId}/reset-app-consent`, {
    method: 'POST',
  });
  return response.json();
}

// ============================================
// Tenant Domains API
// ============================================

export interface TenantDomain {
  domain: string;
  isDefault: boolean;
}

export interface TenantDomainsResponse {
  domains: TenantDomain[];
}

export async function getTenantDomains(clientId: string): Promise<TenantDomainsResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/domains`);
  return response.json();
}

// ============================================
// Tenant Matching API
// ============================================

export interface PartnerCenterCustomer {
  id: string;
  tenantId: string;
  companyName: string;
  domain: string;
  lastSyncedAt: string;
}

export interface UnmatchedClient {
  id: string;
  companyName: string;
  pax8CompanyId?: string;
}

export interface TenantMatchingData {
  partnerCenterCustomers: PartnerCenterCustomer[];
  unmatchedClients: UnmatchedClient[];
}

export async function getTenantMatchingData(): Promise<TenantMatchingData> {
  const response = await fetchWithAuth('/tenant-matching');
  return response.json();
}

export async function linkClientToTenant(clientId: string, tenantId: string): Promise<{ success: boolean; client: Client }> {
  const response = await fetchWithAuth('/tenant-matching/link', {
    method: 'POST',
    body: JSON.stringify({ clientId, tenantId }),
  });
  return response.json();
}

// ============================================
// Security Snapshots API
// ============================================

export interface SecuritySnapshotSecureScore {
  current: number;
  max: number;
  percentage: number;
}

export interface SecuritySnapshotMfa {
  totalUsers: number;
  mfaRegistered: number;
  mfaCapable: number;
  coverage: number;
}

export interface SecuritySnapshotAdmin {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
  mfaRegistered: boolean;
}

export interface SecuritySnapshotAdmins {
  total: number;
  withMfa: number;
  withoutMfa: number;
  list: SecuritySnapshotAdmin[];
}

export interface SecuritySnapshotConditionalAccess {
  totalPolicies: number;
  enabledPolicies: number;
  reportOnlyPolicies: number;
}

export interface SecuritySnapshotMailboxSecurity {
  totalMailboxes: number;
  externalForwardingEnabled: number;
  suspiciousRules: number;
}

export interface SecuritySnapshot {
  id: string;
  clientId: string;
  tenantId: string;
  clientName: string;
  snapshotDate: string;
  secureScore: SecuritySnapshotSecureScore | null;
  mfa: SecuritySnapshotMfa;
  admins: SecuritySnapshotAdmins;
  conditionalAccess: SecuritySnapshotConditionalAccess | null;
  mailboxSecurity: SecuritySnapshotMailboxSecurity | null;
  collectedAt: string;
  collectionDurationMs: number;
  errors: string[] | null;
}

export async function getSecuritySnapshot(clientId: string): Promise<{ success: boolean; data?: SecuritySnapshot; error?: string }> {
  const response = await fetchWithAuth(`/security/client/${clientId}`);
  return response.json();
}

export async function getAllSecuritySnapshots(): Promise<{ success: boolean; data: SecuritySnapshot[]; count: number }> {
  const response = await fetchWithAuth('/security');
  return response.json();
}

// ============================================
// Conditional Access Policy Management API
// ============================================

export interface ConditionalAccessPolicy {
  id: string;
  displayName: string;
  state: 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced';
  createdDateTime: string;
  modifiedDateTime: string;
  conditions: Record<string, unknown>;
  grantControls: Record<string, unknown> | null;
  sessionControls: Record<string, unknown> | null;
}

export async function getConditionalAccessPolicies(clientId: string): Promise<{ success: boolean; policies?: ConditionalAccessPolicy[]; error?: string }> {
  const response = await fetchWithAuth(`/clients/${clientId}/conditional-access`);
  return response.json();
}

export async function updateConditionalAccessPolicy(
  clientId: string,
  policyId: string,
  state: 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced'
): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetchWithAuth(`/clients/${clientId}/conditional-access/${policyId}`, {
    method: 'PATCH',
    body: JSON.stringify({ state }),
  });
  return response.json();
}

export async function createConditionalAccessPolicy(
  clientId: string,
  policy: {
    displayName: string;
    state: 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced';
    conditions: Record<string, unknown>;
    grantControls?: Record<string, unknown>;
    sessionControls?: Record<string, unknown>;
  }
): Promise<{ success: boolean; policy?: ConditionalAccessPolicy; error?: string }> {
  const response = await fetchWithAuth(`/clients/${clientId}/conditional-access`, {
    method: 'POST',
    body: JSON.stringify(policy),
  });
  return response.json();
}

export async function deleteConditionalAccessPolicy(
  clientId: string,
  policyId: string,
  policyName?: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const params = policyName ? `?policyName=${encodeURIComponent(policyName)}` : '';
  const response = await fetchWithAuth(`/clients/${clientId}/conditional-access/${policyId}${params}`, {
    method: 'DELETE',
  });
  return response.json();
}

// ============================================
// MFA Reset API
// ============================================

export interface MfaResetResponse {
  success: boolean;
  message?: string;
  deletedMethods?: string[];
  errors?: string[];
  error?: string;
}

export async function resetUserMfa(clientId: string, userId: string): Promise<MfaResetResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/reset-mfa`, {
    method: 'POST',
  });
  return response.json();
}

// ============================================
// Send Credentials Email API
// ============================================

export interface SendCredentialsEmailRequest {
  recipientEmail: string;
  userName: string;
  userEmail: string;
  tempPassword: string;
  forceChangePassword: boolean;
  licenseName?: string;
  clientName: string;
}

export interface SendCredentialsEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export async function sendCredentialsEmail(request: SendCredentialsEmailRequest): Promise<SendCredentialsEmailResponse> {
  const response = await fetchWithAuth('/send-credentials-email', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// ============================================
// Secure Credentials (TAP + One-time Links)
// ============================================

// TAP-eligible SKUs
export const TAP_ELIGIBLE_SKUS = [
  'AAD_PREMIUM',       // Azure AD P1
  'AAD_PREMIUM_P2',    // Azure AD P2
  'SPB',               // Microsoft 365 Business Premium
  'SPE_E3',            // Microsoft 365 E3
  'SPE_E5',            // Microsoft 365 E5
  'EMSPREMIUM',        // Enterprise Mobility + Security E5
  'EMS',               // Enterprise Mobility + Security E3
  'M365_E3',           // Microsoft 365 E3 (alternate)
  'M365_E5',           // Microsoft 365 E5 (alternate)
];

export interface GenerateTAPRequest {
  tenantId: string;
  userId: string;
  userName: string;
  lifetimeInMinutes?: number;
  isUsableOnce?: boolean;
}

export interface GenerateTAPResponse {
  success: boolean;
  temporaryAccessPass?: string;
  lifetimeInMinutes?: number;
  expiresAt?: string;
  isUsableOnce?: boolean;
  error?: string;
  tapNotEnabled?: boolean;
}

export async function generateTAP(request: GenerateTAPRequest): Promise<GenerateTAPResponse> {
  const response = await fetchWithAuth('/generate-tap', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

export interface EnableTAPResponse {
  success: boolean;
  message?: string;
  alreadyEnabled?: boolean;
  previousState?: string;
  needsReconsent?: boolean;
  error?: string;
}

export async function enableTAP(clientId: string): Promise<EnableTAPResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/enable-tap`, {
    method: 'POST',
  });
  return response.json();
}

export interface SendUserTAPRequest {
  recipientEmail: string;
  recipientPhoneNumber?: string;  // For secure SMS delivery
  lifetimeInMinutes?: number;
  isUsableOnce?: boolean;
  startDateTime?: string;
}

export interface SendUserTAPResponse {
  success: boolean;
  message?: string;
  // Note: TAP code is NOT returned - it's only sent via email/SMS for security
  deliveryMethod?: 'email' | 'split';  // 'split' = email instructions + SMS TAP code
  expiresAt?: string;
  smsError?: string;  // If SMS failed but email succeeded
  user?: {
    id: string;
    displayName: string;
    userPrincipalName: string;
  };
  needsReconsent?: boolean;
  error?: string;
}

export interface GenerateUserTAPRequest {
  lifetimeInMinutes?: number;
  isUsableOnce?: boolean;
  startDateTime?: string;
}

export interface GenerateUserTAPResponse {
  success: boolean;
  temporaryAccessPass?: string;
  lifetimeInMinutes?: number;
  isUsableOnce?: boolean;
  startDateTime?: string;
  expiresAt?: string;
  user?: {
    id: string;
    displayName: string;
    userPrincipalName: string;
  };
  needsReconsent?: boolean;
  error?: string;
}

/**
 * Send a TAP to an existing user.
 * - If phone number provided + RingCentral connected: Instructions via email, TAP code via SMS (more secure)
 * - Otherwise: Everything via email
 */
export async function sendUserTAP(
  clientId: string,
  userId: string,
  recipientEmail: string,
  recipientPhoneNumber?: string,
  lifetimeInMinutes?: number,
  isUsableOnce?: boolean,
  startDateTime?: string
): Promise<SendUserTAPResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/send-tap`, {
    method: 'POST',
    body: JSON.stringify({ recipientEmail, recipientPhoneNumber, lifetimeInMinutes, isUsableOnce, startDateTime } as SendUserTAPRequest),
  });
  return response.json();
}

/**
 * Generate a TAP and return the code on screen.
 * Used for Quick TAP (read to user over phone) and Advanced TAP with "Show on Screen" delivery.
 */
export async function generateUserTAP(
  clientId: string,
  userId: string,
  options?: GenerateUserTAPRequest
): Promise<GenerateUserTAPResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/generate-tap`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
  return response.json();
}

export interface StoreSecureCredentialsRequest {
  userName: string;
  userEmail: string;
  tempPassword: string;
  licenseName?: string;
  clientName: string;
  expiresInHours?: number;
  phoneNumber?: string;     // For SMS verification instead of email
  senderUserId?: string;    // Admin's user ID for RingCentral SMS
}

export interface StoreSecureCredentialsResponse {
  success: boolean;
  token?: string;
  expiresAt?: string;
  expiresInHours?: number;
  error?: string;
}

export async function storeSecureCredentials(request: StoreSecureCredentialsRequest): Promise<StoreSecureCredentialsResponse> {
  const response = await fetchWithAuth('/secure-credentials', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

export interface SendTAPEmailRequest {
  recipientEmail: string;
  userName: string;
  userEmail: string;
  temporaryAccessPass: string;
  expiresAt: string;
  licenseName?: string;
  clientName: string;
}

export async function sendTAPEmail(request: SendTAPEmailRequest): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetchWithAuth('/send-tap-email', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

export interface SendSecureLinkEmailRequest {
  recipientEmail: string;
  userName: string;
  userEmail: string;
  secureLink: string;
  expiresAt: string;
  licenseName?: string;
  clientName: string;
}

export async function sendSecureLinkEmail(request: SendSecureLinkEmailRequest): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetchWithAuth('/send-secure-link-email', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// ============================================
// License Reports
// ============================================

export interface SendLicenseReportRequest {
  recipientEmail: string;
  clientId: string;
  clientName: string;
  reportText: string;
  reportByLicense: string;
  stats: {
    totalUsers: number;
    usersWithLicenses: number;
    usersWithoutLicenses: number;
    totalAssignments: number;
    licenseTypes: number;
    unusedLicenses?: number;
  };
  // Structured data for professional email rendering
  userData?: {
    displayName: string;
    email: string;
    jobTitle: string;
    licenses: string[];
  }[];
  licenseData?: {
    name: string;
    assigned: number;
    total: number;
    unused?: number;
    users: { displayName: string; email: string }[];
  }[];
}

export async function sendLicenseReport(request: SendLicenseReportRequest): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetchWithAuth('/send-license-report', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// ============================================
// Settings API
// ============================================

export interface SmtpSettings {
  host: string;
  port: number;
  user: string;
  password: string;  // Will be masked (e.g., "••••••••••••")
  fromAddress: string;
}

export interface GetSettingsResponse {
  smtp: SmtpSettings | null;
  configured: boolean;
}

export interface SaveSettingsRequest {
  smtp: {
    host: string;
    port: number;
    user: string;
    password?: string;  // Only send if changed
    fromAddress: string;
  };
}

export async function getSettings(): Promise<GetSettingsResponse> {
  const response = await fetchWithAuth('/settings');
  return response.json();
}

export async function saveSettings(request: SaveSettingsRequest): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth('/settings', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

export async function testSmtpConnection(smtp?: SmtpSettings): Promise<{ success: boolean; error?: string }> {
  const response = await fetchWithAuth('/settings/test-smtp', {
    method: 'POST',
    body: JSON.stringify(smtp ? { smtp } : {}),
  });
  return response.json();
}

// ============================================
// RingCentral Integration API
// ============================================

export interface RingCentralStatusResponse {
  success: boolean;
  connected: boolean;
  phoneNumber?: string;
  extensionName?: string;
  connectedAt?: string;
  needsReconnect?: boolean;
  error?: string;
}

export async function getRingCentralAuthUrl(): Promise<{ success: boolean; authUrl?: string; error?: string }> {
  const response = await fetchWithAuth('/ringcentral/auth-url');
  return response.json();
}

export async function getRingCentralStatus(): Promise<RingCentralStatusResponse> {
  const response = await fetchWithAuth('/ringcentral/status');
  return response.json();
}

export async function disconnectRingCentral(): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetchWithAuth('/ringcentral/disconnect', {
    method: 'POST',
  });
  return response.json();
}

export async function updateRingCentralPhone(phoneNumber: string): Promise<{ success: boolean; phoneNumber?: string; error?: string }> {
  const response = await fetchWithAuth('/ringcentral/phone', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber }),
  });
  return response.json();
}

// ============================================
// GDAP Client Onboarding API
// ============================================

export interface GDAPRole {
  id: string;
  name: string;
}

export interface CreateGDAPRelationshipRequest {
  companyName: string;
  customerTenantId?: string;
}

export interface CreateGDAPRelationshipResponse {
  relationshipId: string;
  displayName: string;
  status: string;
  approvalUrl: string;
  roles: GDAPRole[];
  roleCount: number;
  duration: {
    days: number;
    autoExtendDays: number;
  };
  instructions: {
    step1: string;
    step2: string;
    step3: string;
    step4: string;
  };
  existing?: boolean;
  error?: string;
  details?: string;
}

export async function createGDAPRelationship(data: CreateGDAPRelationshipRequest): Promise<CreateGDAPRelationshipResponse> {
  const response = await fetchWithAuth('/gdap/relationships', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export interface ClientGDAPStatusResponse {
  hasRelationship: boolean;
  status: string;
  statusDescription: string;
  relationshipId?: string;
  displayName?: string;
  createdDateTime?: string;
  activatedDateTime?: string | null;
  endDateTime?: string | null;
  roleCount?: number;
  companyName: string;
  tenantId: string;
  message?: string;
  relationships?: Array<{
    id: string;
    displayName: string;
    status: string;
    createdDateTime: string;
    endDateTime: string | null;
    roleCount: number;
  }>;
}

export async function getClientGDAPStatus(clientId: string): Promise<ClientGDAPStatusResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/gdap-status`);
  return response.json();
}

export interface GDAPRelationshipStatusResponse {
  id: string;
  displayName: string;
  status: string;
  statusDescription: string;
  customer: {
    tenantId: string;
    displayName: string;
  } | null;
  createdDateTime: string;
  activatedDateTime: string | null;
  endDateTime: string | null;
  roleCount: number;
  roles: GDAPRole[];
  accessAssignments: Array<{
    groupId: string;
    groupName: string;
    status: string;
    roleCount: number;
  }>;
  hasAdminAgentsAssigned: boolean;
  approvalUrl: string;
  nextAction: {
    action: string;
    description: string;
  };
  error?: string;
}

export async function getGDAPRelationshipStatus(relationshipId: string): Promise<GDAPRelationshipStatusResponse> {
  const response = await fetchWithAuth(`/gdap/relationships/${relationshipId}`);
  return response.json();
}

export interface AssignAdminAgentsResponse {
  success: boolean;
  alreadyAssigned: boolean;
  assignmentId?: string;
  groupId?: string;
  groupName?: string;
  roleCount?: number;
  relationshipStatus?: string;
  customer?: {
    tenantId: string;
    displayName: string;
  };
  message?: string;
  note?: string;
  roles?: string[];
  error?: string;
  details?: string;
}

export async function assignAdminAgents(relationshipId: string): Promise<AssignAdminAgentsResponse> {
  const response = await fetchWithAuth(`/gdap/relationships/${relationshipId}/assign-adminagents`, {
    method: 'POST',
  });
  return response.json();
}

export interface GDAPConsentUrlResponse {
  consentUrl: string;
  appId: string;
  appName: string;
  tenantId: string;
  permissions: Array<{
    name: string;
    description: string;
  }>;
  instructions: string[];
  note: string;
  error?: string;
}

export async function getGDAPConsentUrl(tenantId: string): Promise<GDAPConsentUrlResponse> {
  const response = await fetchWithAuth(`/gdap/consent-url?tenantId=${encodeURIComponent(tenantId)}`);
  return response.json();
}

// ============================================
// Shared Mailbox API
// ============================================

import {
  SharedMailbox,
  CreateSharedMailboxRequest,
  MailboxDelegate,
  ManageMailboxPermissionRequest,
  OneDriveShare,
  GrantOneDriveAccessRequest,
} from '../types';

export interface GetSharedMailboxesResponse {
  success: boolean;
  clientId: string;
  clientName: string;
  mailboxes: SharedMailbox[];
  count: number;
  error?: string;
}

export async function getSharedMailboxes(clientId: string): Promise<GetSharedMailboxesResponse> {
  // Use SWA proxy for proper auth (cookies)
  // May timeout at 45s for slow runbooks, but auth will work
  await logDebug('request', 'System', `GET /clients/${clientId}/shared-mailboxes`, 'Fetching shared mailboxes (may take 60-90s)...');
  try {
    const response = await fetchWithAuth(`/clients/${clientId}/shared-mailboxes`);
    const data = await response.json();
    await logDebug('success', 'System', `Got ${data.count || 0} shared mailboxes`);
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    // If it's a timeout, the operation may still be running
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Request timed out - operation may still be in progress');
      throw new Error('Request timed out. The operation may still be running - please refresh in a minute.');
    }
    throw error;
  }
}

export interface CreateSharedMailboxResponse {
  success: boolean;
  mailboxCreated: boolean;
  mailbox?: {
    displayName: string;
    email: string;
    alias: string;
  };
  ownersAdded?: string[];
  ownerErrors?: string[];
  message?: string;
  error?: string;
}

export async function createSharedMailbox(
  clientId: string,
  data: CreateSharedMailboxRequest
): Promise<CreateSharedMailboxResponse> {
  // Use SWA proxy for proper auth (cookies)
  await logDebug('request', 'System', `POST /clients/${clientId}/shared-mailboxes`, `Creating mailbox: ${data.displayName}`);
  try {
    const response = await fetchWithAuth(`/clients/${clientId}/shared-mailboxes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    if (result.success) {
      await logDebug('success', 'System', `Mailbox created: ${result.mailbox?.email}`);
    }
    return result;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Request timed out - mailbox creation may still be in progress');
      throw new Error('Request timed out. The mailbox may still be creating - please check back in a minute.');
    }
    throw error;
  }
}

// ============================================
// Delete Shared Mailbox API
// ============================================

export interface DeleteSharedMailboxResponse {
  success: boolean;
  mailboxDeleted?: boolean;
  message?: string;
  error?: string;
}

export async function deleteSharedMailbox(
  clientId: string,
  email: string
): Promise<DeleteSharedMailboxResponse> {
  await logDebug('request', 'System', `DELETE /clients/${clientId}/shared-mailboxes/${encodeURIComponent(email)}`, `Deleting mailbox: ${email}`);
  try {
    const response = await fetchWithAuth(`/clients/${clientId}/shared-mailboxes/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
    const result = await response.json();
    if (result.success) {
      await logDebug('success', 'System', `Mailbox deleted: ${email}`);
    }
    return result;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Request timed out - mailbox deletion may still be in progress');
      throw new Error('Request timed out. The mailbox may still be deleting - please check back in a minute.');
    }
    throw error;
  }
}

// ============================================
// Mailbox Forwarding API
// ============================================

export interface ManageMailboxForwardingRequest {
  action: 'set' | 'remove';
  forwardingAddress?: string;
  deliverToMailboxAndForward?: boolean;
}

export interface ManageMailboxForwardingResponse {
  success: boolean;
  action?: string;
  forwardingAddress?: string | null;
  deliverToMailboxAndForward?: boolean;
  message?: string;
  error?: string;
}

export async function manageMailboxForwarding(
  clientId: string,
  userId: string,
  userUpn: string,
  data: ManageMailboxForwardingRequest
): Promise<ManageMailboxForwardingResponse> {
  await logDebug('request', 'System', `POST /clients/${clientId}/users/${userId}/forwarding`, `${data.action} forwarding for ${userUpn}`);
  try {
    const response = await fetchWithAuth(
      `/clients/${clientId}/users/${userId}/forwarding?upn=${encodeURIComponent(userUpn)}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    const result = await response.json();
    if (result.success) {
      await logDebug('success', 'System', `Forwarding ${data.action}: ${result.message}`);
    }
    return result;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Request timed out - forwarding change may still be in progress');
      throw new Error('Request timed out. The operation may still be running - please check back in a minute.');
    }
    throw error;
  }
}

// ============================================
// Out of Office API
// ============================================

export interface ManageOutOfOfficeRequest {
  action: 'enable' | 'disable';
  internalMessage?: string;
  externalMessage?: string;
  startDate?: string;
  endDate?: string;
}

export interface ManageOutOfOfficeResponse {
  success: boolean;
  action?: string;
  autoReplyState?: string;
  internalMessage?: string;
  externalMessage?: string;
  startDate?: string;
  endDate?: string;
  message?: string;
  error?: string;
}

export async function manageOutOfOffice(
  clientId: string,
  userId: string,
  userUpn: string,
  data: ManageOutOfOfficeRequest
): Promise<ManageOutOfOfficeResponse> {
  await logDebug('request', 'System', `POST /clients/${clientId}/users/${userId}/out-of-office`, `${data.action} OOO for ${userUpn}`);
  try {
    const response = await fetchWithAuth(
      `/clients/${clientId}/users/${userId}/out-of-office?upn=${encodeURIComponent(userUpn)}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    const result = await response.json();
    if (result.success) {
      await logDebug('success', 'System', `Out of office ${data.action}: ${result.message}`);
    }
    return result;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Request timed out - out of office change may still be in progress');
      throw new Error('Request timed out. The operation may still be running - please check back in a minute.');
    }
    throw error;
  }
}

// ============================================
// Mailbox Permissions API
// ============================================

export interface GetMailboxPermissionsResponse {
  success: boolean;
  mailboxEmail: string;
  mailboxDisplayName?: string;
  delegates: MailboxDelegate[];
  error?: string;
}

export async function getMailboxPermissions(
  clientId: string,
  userId: string,
  mailboxEmail: string
): Promise<GetMailboxPermissionsResponse> {
  // Use SWA proxy for proper auth
  const response = await fetchWithAuth(
    `/clients/${clientId}/users/${userId}/mailbox-permissions?mailboxEmail=${encodeURIComponent(mailboxEmail)}`
  );
  return response.json();
}

export interface ManageMailboxPermissionsResponse {
  success: boolean;
  action?: string;
  permissionsModified?: string[];
  errors?: string[];
  message?: string;
  error?: string;
}

export async function manageMailboxPermissions(
  clientId: string,
  userId: string,
  mailboxEmail: string,
  data: ManageMailboxPermissionRequest
): Promise<ManageMailboxPermissionsResponse> {
  // Use SWA proxy for proper auth
  const response = await fetchWithAuth(
    `/clients/${clientId}/users/${userId}/mailbox-permissions?mailboxEmail=${encodeURIComponent(mailboxEmail)}`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.json();
}

// ============================================
// Batch Mailbox Changes API
// ============================================

export interface BatchPermissionChange {
  action: 'add' | 'remove';
  delegateEmail: string;
  fullAccess?: boolean;
  sendAs?: boolean;
  sendOnBehalf?: boolean;
}

export interface BatchForwardingChange {
  action: 'set' | 'remove';
  forwardingAddress?: string;
  deliverToMailboxAndForward?: boolean;
}

export interface BatchOutOfOfficeChange {
  action: 'enable' | 'disable';
  internalMessage?: string;
  externalMessage?: string;
  startDate?: string;
  endDate?: string;
}

export interface BatchMailboxChangesRequest {
  permissions?: BatchPermissionChange[];
  forwarding?: BatchForwardingChange;
  outOfOffice?: BatchOutOfOfficeChange;
}

export interface BatchMailboxChangesResponse {
  success: boolean;
  results?: Record<string, unknown>;
  error?: string;
  jobId?: string;
}

export async function batchMailboxChanges(
  clientId: string,
  userId: string,
  upn: string,
  data: BatchMailboxChangesRequest
): Promise<BatchMailboxChangesResponse> {
  const response = await fetchWithAuth(
    `/clients/${clientId}/users/${userId}/mailbox-batch?upn=${encodeURIComponent(upn)}`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.json();
}

// ============================================
// OneDrive Sharing API
// ============================================

export interface GetOneDriveSharesResponse {
  success: boolean;
  userId: string;
  shares: OneDriveShare[];
  count: number;
  error?: string;
}

export async function getOneDriveShares(clientId: string, userId: string): Promise<GetOneDriveSharesResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/onedrive-shares`);
  return response.json();
}

export interface GrantOneDriveAccessResponse {
  success: boolean;
  delegateEmail?: string;
  accessLevel?: string;
  shareLink?: string;
  message?: string;
  error?: string;
}

export async function grantOneDriveAccess(
  clientId: string,
  userId: string,
  data: GrantOneDriveAccessRequest
): Promise<GrantOneDriveAccessResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/users/${userId}/onedrive-shares`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export interface UpdateOneDriveAccessResponse {
  success: boolean;
  permissionId?: string;
  accessLevel?: string;
  message?: string;
  error?: string;
}

export async function updateOneDriveAccess(
  clientId: string,
  userId: string,
  permissionId: string,
  accessLevel: 'read' | 'write'
): Promise<UpdateOneDriveAccessResponse> {
  const response = await fetchWithAuth(
    `/clients/${clientId}/users/${userId}/onedrive-shares/${permissionId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ accessLevel }),
    }
  );
  return response.json();
}

export interface RevokeOneDriveAccessResponse {
  success: boolean;
  permissionId?: string;
  message?: string;
  error?: string;
}

export async function revokeOneDriveAccess(
  clientId: string,
  userId: string,
  permissionId: string
): Promise<RevokeOneDriveAccessResponse> {
  const response = await fetchWithAuth(
    `/clients/${clientId}/users/${userId}/onedrive-shares/${permissionId}`,
    {
      method: 'DELETE',
    }
  );
  return response.json();
}

// ============================================
// Client Sync Data API (cached MS tenant data)
// ============================================

import {
  GetSyncDataResponse,
  TriggerSyncResponse,
  ClientSyncData,
} from '../types';

/**
 * Get cached sync data for a client
 * Returns instantly from Cosmos DB cache
 */
export async function getClientSyncData(clientId: string): Promise<GetSyncDataResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/sync-data`);
  return response.json();
}

/**
 * Trigger a full sync of MS data for a client
 * This calls Graph API and runbooks, may take 1-2 minutes
 * Uses SWA proxy for proper authentication
 */
export async function triggerClientSync(clientId: string): Promise<TriggerSyncResponse> {
  await logDebug('request', 'Sync', `POST /clients/${clientId}/sync-ms-data`, 'Starting MS data sync...');

  try {
    // Use SWA-proxied endpoint for proper auth (x-ms-client-principal header)
    // Note: May timeout after 45s but sync continues in background
    const response = await fetchWithAuth(`/clients/${clientId}/sync-ms-data`, {
      method: 'POST',
    });

    const data = await response.json();

    if (data.success) {
      await logDebug('success', 'Sync', `Sync completed for ${clientId}`, JSON.stringify(data, null, 2));
    } else {
      await logDebug('error', 'Sync', `Sync failed: ${data.error || 'Unknown error'}`, JSON.stringify(data, null, 2));
    }

    return data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    await logDebug('error', 'Sync', `Sync request failed: ${errorMessage}`, error instanceof Error ? error.stack : undefined);
    throw error;
  }
}

/**
 * Update sync schedule settings for a client
 */
export async function updateClientSyncSchedule(
  clientId: string,
  enabled: boolean,
  intervalHours: number
): Promise<{ success: boolean; syncSchedule: ClientSyncData['syncSchedule'] }> {
  const response = await fetchWithAuth(`/clients/${clientId}/sync-schedule`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled, intervalHours }),
  });
  return response.json();
}

// ============================================
// Renewal Calendar API
// ============================================

export type CalendarEventCategory = 'renewal' | 'reminder' | 'action-required' | 'follow-up' | 'migration';

export interface CalendarEvent {
  id: string;
  partitionKey: 'calendar-event';
  title: string;
  description: string;
  date: string;  // YYYY-MM-DD
  category: CalendarEventCategory;
  clientId: string | null;
  clientName: string | null;
  subscriptionId: string | null;
  subscriptionName: string | null;
  isAutoGenerated: boolean;
  renewalAmount: number | null;
  createdAt: string;
  createdBy: string;
  createdByEmail: string;
  modifiedAt: string | null;
  modifiedBy: string | null;
  modifiedByEmail: string | null;
}

export interface SubscriptionRenewalEvent {
  id: string;
  type: 'renewal';
  date: string;
  clientId: string;
  clientName: string;
  subscriptionId: string;
  productName: string;
  quantity: number;
  totalPrice: number;
  billingTerm: 'annual' | 'monthly';
  autoRenew: boolean | null;
  source: 'pax8' | 'direct' | 'manual';
}

export interface CalendarMonthData {
  year: number;
  month: number;
  events: CalendarEvent[];
  renewals: SubscriptionRenewalEvent[];
  byDate: Record<string, (CalendarEvent | SubscriptionRenewalEvent)[]>;
}

export interface CreateCalendarEventRequest {
  title: string;
  description?: string;
  date: string;
  category: CalendarEventCategory;
  clientId?: string;
  clientName?: string;
  subscriptionId?: string;
  subscriptionName?: string;
}

export interface UpdateCalendarEventRequest {
  title?: string;
  description?: string;
  date?: string;
  category?: CalendarEventCategory;
}

export interface CalendarEventFilters {
  fromDate?: string;
  toDate?: string;
  category?: CalendarEventCategory;
  clientId?: string;
}

/**
 * Get calendar data for a specific month
 * Returns both manual events and subscription renewals
 */
export async function getCalendarMonth(year: number, month: number): Promise<CalendarMonthData> {
  const response = await fetchWithAuth(`/calendar/month/${year}/${month}`);
  return response.json();
}

/**
 * Get calendar events with optional filters
 */
export async function getCalendarEvents(filters: CalendarEventFilters = {}): Promise<{ events: CalendarEvent[] }> {
  const params = new URLSearchParams();
  if (filters.fromDate) params.append('fromDate', filters.fromDate);
  if (filters.toDate) params.append('toDate', filters.toDate);
  if (filters.category) params.append('category', filters.category);
  if (filters.clientId) params.append('clientId', filters.clientId);

  const response = await fetchWithAuth(`/calendar/events?${params.toString()}`);
  return response.json();
}

/**
 * Create a new calendar event
 */
export async function createCalendarEvent(data: CreateCalendarEventRequest): Promise<CalendarEvent> {
  const response = await fetchWithAuth('/calendar/events', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Update an existing calendar event
 */
export async function updateCalendarEvent(id: string, data: UpdateCalendarEventRequest): Promise<CalendarEvent> {
  const response = await fetchWithAuth(`/calendar/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Delete a calendar event
 */
export async function deleteCalendarEvent(id: string): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth(`/calendar/events/${id}`, {
    method: 'DELETE',
  });
  return response.json();
}

// ============================================
// Sign-In Logs & Anomaly Detection
// ============================================

export interface SignInLogEntry {
  id: string;
  userId: string;
  userPrincipalName: string;
  userDisplayName: string;
  timestamp: string;
  ipAddress: string;
  location: {
    city: string | null;
    state: string | null;
    countryOrRegion: string | null;
    geoCoordinates?: {
      latitude: number;
      longitude: number;
    } | null;
  };
  deviceDetail: {
    browser: string | null;
    operatingSystem: string | null;
    deviceId: string | null;
    displayName: string | null;
    isCompliant: boolean | null;
    isManaged: boolean | null;
    trustType: string | null;
  };
  appDisplayName: string;
  clientAppUsed: string;
  status: {
    errorCode: number;
    failureReason: string | null;
    additionalDetails: string | null;
  };
  riskLevel: 'none' | 'low' | 'medium' | 'high' | 'hidden' | 'unknownFutureValue';
  riskState: 'none' | 'confirmedSafe' | 'remediated' | 'dismissed' | 'atRisk' | 'confirmedCompromised' | 'unknownFutureValue';
  isInteractive: boolean;
  anomalies?: SignInAnomaly[];
}

export interface SignInAnomaly {
  type: 'new_location' | 'impossible_travel' | 'failed_login_spike' | 'new_device' | 'off_hours' | 'risky_signin' | 'new_app';
  severity: 'low' | 'medium' | 'high';
  description: string;
  details?: Record<string, unknown>;
}

export interface GetSignInLogsResponse {
  success: boolean;
  clientId: string;
  tenantId: string;
  logs: SignInLogEntry[];
  summary: {
    totalLogs: number;
    failedLogins: number;
    riskySignIns: number;
    anomaliesDetected: number;
    uniqueLocations: number;
    uniqueDevices: number;
  };
  hasMore: boolean;
  error?: string;
}

/**
 * Get sign-in logs for a client tenant (all users or specific user)
 */
export async function getClientSignInLogs(
  clientId: string,
  options: { userId?: string; top?: number; includeNonInteractive?: boolean } = {}
): Promise<GetSignInLogsResponse> {
  const params = new URLSearchParams();
  if (options.userId) params.append('userId', options.userId);
  if (options.top) params.append('top', options.top.toString());
  if (options.includeNonInteractive) params.append('includeNonInteractive', 'true');

  const response = await fetchWithAuth(`/clients/${clientId}/sign-in-logs?${params.toString()}`);
  return response.json();
}

// ============================================
// Diagnostics
// ============================================

export interface DiagnoseSkuResult {
  clientId: string;
  clientName: string;
  tenantId: string | null;
  directSubscriptions: {
    id: string;
    productName: string;
    sku: string;
    quantity: number;
    msSkuId: string | null | undefined;
    msSkuPartNumber: string | null | undefined;
  }[];
  msLicenses: {
    skuId: string;
    skuPartNumber: string;
    displayName: string;
    totalLicenses: number;
    assignedLicenses: number;
  }[];
  matchingAttempts: {
    subscription: string;
    licenseSkuPartNumber: string;
    matchType: string;
    matched: boolean;
  }[];
  fixes: {
    subscriptionId: string;
    subscriptionName: string;
    matchedSkuId: string;
    matchedSkuPartNumber: string;
    matchReason: string;
  }[];
  error?: string;
}

/**
 * Diagnose msSkuId matching for a client's subscriptions
 * @param clientId - The client ID
 * @param fix - Whether to fix missing msSkuIds (default false)
 */
export async function diagnoseMsSkuId(clientId: string, fix: boolean = false): Promise<DiagnoseSkuResult> {
  const response = await fetchWithAuth(`/clients/${clientId}/diagnose-sku${fix ? '?fix=true' : ''}`);
  return response.json();
}

// ============================================
// Message Trace
// ============================================

export interface MessageTraceResult {
  messageTraceId: string;
  messageId: string;
  senderAddress: string;
  recipientAddress: string;
  subject: string;
  status: string;
  toIP: string;
  fromIP: string;
  size: number;
  received: string;
}

export interface MessageTraceFilters {
  senderAddress?: string;
  recipientAddress?: string;
  startDate?: string;
  endDate?: string;
  messageId?: string;
  status?: string;
}

export interface GetMessageTraceResponse {
  success: boolean;
  clientId: string;
  clientName: string;
  messages: MessageTraceResult[];
  totalCount: number;
  error?: string;
}

export async function getMessageTrace(clientId: string, filters: MessageTraceFilters = {}): Promise<GetMessageTraceResponse> {
  const params = new URLSearchParams();
  if (filters.senderAddress) params.append('senderAddress', filters.senderAddress);
  if (filters.recipientAddress) params.append('recipientAddress', filters.recipientAddress);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.messageId) params.append('messageId', filters.messageId);
  if (filters.status) params.append('status', filters.status);

  const queryString = params.toString();
  const url = `/clients/${clientId}/message-trace${queryString ? `?${queryString}` : ''}`;

  await logDebug('request', 'System', `GET ${url}`, 'Running message trace (may take 30-60s)...');
  try {
    const response = await fetchWithAuth(url);
    const data = await response.json();
    await logDebug('success', 'System', `Message trace returned ${data.totalCount || 0} messages`);
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Message trace timed out - operation may still be running');
      throw new Error('Request timed out. The message trace may still be running - please try again in a minute.');
    }
    throw error;
  }
}

export interface MessageTraceEvent {
  date: string;
  event: string;
  action: string;
  detail: string;
  data: string;
}

export interface GetMessageTraceDetailResponse {
  success: boolean;
  messageTraceId: string;
  events: MessageTraceEvent[];
  error?: string;
}

export async function getMessageTraceDetail(
  clientId: string,
  messageTraceId: string,
  recipientAddress: string,
  messageId?: string
): Promise<GetMessageTraceDetailResponse> {
  const params = new URLSearchParams();
  params.append('recipientAddress', recipientAddress);
  if (messageId) params.append('messageId', messageId);

  const url = `/clients/${clientId}/message-trace/${messageTraceId}?${params.toString()}`;

  await logDebug('request', 'System', `GET ${url}`, 'Fetching message trace detail...');
  try {
    const response = await fetchWithAuth(url);
    const data = await response.json();
    await logDebug('success', 'System', `Got ${data.events?.length || 0} routing events`);
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  }
}

// ==================== Email Authentication DNS Check ====================

export interface EmailAuthSpfResult {
  found: boolean;
  record?: string;
  valid: boolean;
}

export interface EmailAuthDkimResult {
  found: boolean;
  selector1?: string;
  selector2?: string;
}

export interface EmailAuthDmarcResult {
  found: boolean;
  record?: string;
  policy?: string;
}

export interface EmailAuthDomainResult {
  domain: string;
  isDefault: boolean;
  spf: EmailAuthSpfResult;
  dkim: EmailAuthDkimResult;
  dmarc: EmailAuthDmarcResult;
}

export interface EmailAuthResponse {
  success: boolean;
  clientId: string;
  clientName: string;
  domains: EmailAuthDomainResult[];
  error?: string;
}

export async function checkEmailAuth(clientId: string, domain?: string): Promise<EmailAuthResponse> {
  const params = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  const url = `/clients/${clientId}/email-auth${params}`;
  const response = await fetchWithAuth(url);
  return response.json();
}

// ============================================
// SharePoint Site Permission Management
// ============================================

export interface SharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  isTeamSite: boolean;
}

export interface SharePointSitesResponse {
  success: boolean;
  clientId: string;
  clientName: string;
  sites: SharePointSite[];
  count: number;
  error?: string;
}

export interface SiteGroupMember {
  email: string;
  displayName: string;
  loginName: string;
}

export interface SitePermissionGroup {
  name: string;
  roleType: string;
  memberCount: number;
  members: SiteGroupMember[];
}

export interface SitePermissionsResponse {
  success: boolean;
  clientId: string;
  siteUrl: string;
  groups: SitePermissionGroup[];
  count: number;
  error?: string;
}

export interface ManageSitePermissionRequest {
  siteUrl: string;
  userEmail: string;
  groupName: string;
  action: 'add' | 'remove';
}

export interface ManageSitePermissionResponse {
  success: boolean;
  action?: string;
  user?: string;
  group?: string;
  message?: string;
  error?: string;
}

export async function getSharePointSites(clientId: string): Promise<SharePointSitesResponse> {
  await logDebug('request', 'System', `GET /clients/${clientId}/sharepoint-sites`, 'Fetching SharePoint sites...');
  const response = await fetchWithAuth(`/clients/${clientId}/sharepoint-sites`);
  const data = await response.json();
  await logDebug('success', 'System', `Got ${data.count || 0} SharePoint sites`);
  return data;
}

export async function getSitePermissions(clientId: string, siteUrl: string): Promise<SitePermissionsResponse> {
  await logDebug('request', 'System', `GET /clients/${clientId}/sharepoint-sites/permissions`, 'Fetching site permissions (may take 30-60s)...');
  try {
    const response = await fetchWithAuth(`/clients/${clientId}/sharepoint-sites/permissions?siteUrl=${encodeURIComponent(siteUrl)}`);
    const data = await response.json();
    await logDebug('success', 'System', `Got ${data.count || 0} permission groups`);
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('500') || msg.includes('504') || msg.includes('timeout')) {
      await logDebug('warning', 'System', 'Request timed out - operation may still be in progress');
      throw new Error('Request timed out. The operation may still be running - please try again in a minute.');
    }
    throw error;
  }
}

export async function manageSitePermission(clientId: string, data: ManageSitePermissionRequest): Promise<ManageSitePermissionResponse> {
  await logDebug('request', 'System', `POST /clients/${clientId}/sharepoint-sites/permissions`, `${data.action} ${data.userEmail} in ${data.groupName}`);
  const response = await fetchWithAuth(`/clients/${clientId}/sharepoint-sites/permissions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// ============================================
// Security Operations Center API
// ============================================

export interface ForensicDataSummary {
  collectionId: string;
  collectedAt: string;
  trigger: string;
  phase: string;
  userCount: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  findingCount?: number;
  criticalCount?: number;
}

export interface ForensicDataResponse {
  success: boolean;
  clientId: string;
  clientName: string;
  collections: ForensicDataSummary[];
  latestCollection?: any;
}

export interface CollectForensicDataRequest {
  clientId: string;
  targetUsers?: string[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
}

export interface CollectForensicDataResponse {
  success: boolean;
  collectionId: string;
  clientId: string;
  clientName: string;
  phase: string;
  userCount: number;
  errorCount: number;
  message: string;
  error?: string;
  findingCount?: number;
  deltaFindingCount?: number;
  alertCreated?: boolean;
}

export interface SecurityFinding {
  id: string;
  clientId: string;
  clientName: string;
  tenantDomain: string;
  userId?: string;
  collectionId: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  summary: string;
  details: string;
  rawData?: any;
  detectedAt: string;
  source: string;
  status: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  notes?: string;
  createdDate: string;
}

export interface SecurityFindingsResponse {
  success: boolean;
  clientId: string;
  findings: SecurityFinding[];
  total: number;
  bySeverity: { critical: number; warning: number; info: number };
  byType: Record<string, number>;
}

export interface QuarantineEvent {
  action: 'block_sign_in' | 'revoke_sessions' | 'disable_rule';
  userId: string;
  performedAt: string;
  performedBy: string;
  success: boolean;
  error?: string;
  reversed?: boolean;
  reversedAt?: string;
  reversedBy?: string;
}

export interface SecurityAlertItem {
  id: string;
  clientId: string;
  clientName: string;
  tenantDomain: string;
  findingIds: string[];
  severity: 'critical' | 'warning' | 'info';
  title: string;
  summary: string;
  affectedUsers: string[];
  iocTypes: string[];
  status: string;
  autoQuarantineApplied: boolean;
  quarantineActions?: QuarantineEvent[];
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  notes?: string;
  createdDate: string;
}

export interface SecurityAlertsResponse {
  success: boolean;
  alerts: SecurityAlertItem[];
  total: number;
  bySeverity: { critical: number; warning: number; info: number };
  byStatus: { new: number; acknowledged: number; investigating: number; resolved: number; false_positive: number };
  clientId?: string;
  clientName?: string;
}

export interface IPGeolocationResult {
  success: boolean;
  ip: string;
  geolocation: {
    ip: string;
    city: string;
    state: string;
    country: string;
    countryCode?: string;
    isp?: string;
    org?: string;
    lat?: number;
    lon?: number;
  } | null;
  threatIntel?: {
    source: string;
    abuseScore?: number;
    confidence?: number;
    threatType?: string;
  } | null;
}

// Trigger forensic data collection for a client
export async function collectForensicData(data: CollectForensicDataRequest): Promise<CollectForensicDataResponse> {
  const response = await fetchWithAuth('/security/collect', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Test delta detection for a client (compares 2 most recent forensic docs)
export async function testDeltaEngine(clientId: string, save = false): Promise<any> {
  const response = await fetchWithAuth(`/security/clients/${clientId}/test-delta${save ? '?save=true' : ''}`);
  return response.json();
}

// Get forensic data collections for a client
export async function getForensicData(clientId: string): Promise<ForensicDataResponse> {
  const response = await fetchWithAuth(`/security/clients/${clientId}/forensic-data`);
  return response.json();
}

// Get security findings for a client
export async function getSecurityFindings(
  clientId: string,
  params?: { severity?: string; type?: string; status?: string; limit?: number }
): Promise<SecurityFindingsResponse> {
  const query = new URLSearchParams();
  if (params?.severity) query.set('severity', params.severity);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const response = await fetchWithAuth(`/security/clients/${clientId}/findings${qs ? '?' + qs : ''}`);
  return response.json();
}

// Get all security alerts (cross-client)
export async function getAllSecurityAlerts(
  params?: { status?: string; severity?: string; limit?: number }
): Promise<SecurityAlertsResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const response = await fetchWithAuth(`/security/alerts${qs ? '?' + qs : ''}`);
  return response.json();
}

// Get security alerts for a specific client
export async function getClientSecurityAlerts(
  clientId: string,
  params?: { status?: string; severity?: string; limit?: number }
): Promise<SecurityAlertsResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const response = await fetchWithAuth(`/security/clients/${clientId}/alerts${qs ? '?' + qs : ''}`);
  return response.json();
}

// Update security alert status
export async function updateSecurityAlert(
  alertId: string,
  clientId: string,
  data: { status: string; notes?: string }
): Promise<{ success: boolean; alert: SecurityAlertItem }> {
  const response = await fetchWithAuth(`/security/alerts/${alertId}?clientId=${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Look up IP geolocation and threat intel
export async function getIPGeolocation(ip: string): Promise<IPGeolocationResult> {
  const response = await fetchWithAuth(`/security/ip/${encodeURIComponent(ip)}`);
  return response.json();
}

// Release quarantine for a user
export async function releaseQuarantine(data: {
  clientId: string;
  userId: string;
  alertId?: string;
  notes?: string;
}): Promise<{ success: boolean; userId: string; clientName: string; action: any }> {
  const response = await fetchWithAuth('/security/quarantine/release', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Quarantine a user manually
export async function quarantineUser(data: {
  clientId: string;
  userId: string;
  reason?: string;
}): Promise<{ success: boolean; userId: string; clientName: string; actions: any[]; alertId: string }> {
  const response = await fetchWithAuth('/security/quarantine', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Dismiss/update a security finding status
export async function updateSecurityFinding(
  findingId: string,
  data: { clientId: string; status: string; notes?: string }
): Promise<{ success: boolean; finding: SecurityFinding }> {
  const response = await fetchWithAuth(`/security/findings/${findingId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Disable an inbox rule via Graph API
export async function disableInboxRule(data: {
  clientId: string;
  userId: string;
  ruleId?: string;
  ruleName?: string;
}): Promise<{ success: boolean; ruleId: string; ruleName: string }> {
  const response = await fetchWithAuth('/security/rules/disable', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// --- Security Alert Notification Settings ---

export interface SecurityAlertNotificationSettings {
  enabled: boolean;
  recipients: string[];
  minSeverity: 'critical' | 'warning' | 'all';
  modifiedAt?: string;
  modifiedBy?: string;
}

export async function getSecurityAlertNotificationSettings(): Promise<{ success: boolean; settings: SecurityAlertNotificationSettings }> {
  const response = await fetchWithAuth('/security/alert-notification-settings');
  return response.json();
}

export async function saveSecurityAlertNotificationSettings(
  settings: { enabled: boolean; recipients: string[]; minSeverity: 'critical' | 'warning' | 'all' }
): Promise<{ success: boolean; settings: SecurityAlertNotificationSettings }> {
  const response = await fetchWithAuth('/security/alert-notification-settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
  return response.json();
}

// --- Security Investigations ---

export interface InvestigationItem {
  id: string;
  partitionKey: string;
  clientId: string;
  clientName: string;
  tenantDomain: string;
  templateId?: string;
  title: string;
  description?: string;
  status: 'pending' | 'collecting' | 'analyzing' | 'complete' | 'failed';
  targetUsers: string[];
  dateRangeStart: string;
  dateRangeEnd: string;
  dataSources: string[];
  iocCategories: string[];
  collectionId?: string;
  findingIds: string[];
  alertIds: string[];
  knownAttackerIPs: string[];
  expectedCountries: string[];
  ipAllowlist: string[];
  timeline: Array<{
    date: string;
    event: string;
    severity: 'critical' | 'warning' | 'info';
    user?: string;
    source: 'auto' | 'manual' | 'ai';
    category?: string;
  }>;
  aiAnalysis?: {
    model: string;
    analyzedAt: string;
    threatLevel: string;
    summary: string;
    findings: Array<{
      severity: string;
      category: string;
      description: string;
      evidence: string[];
      recommendedAction: string;
    }>;
    recommendations: string[];
    confidence: number;
  };
  startedAt?: string;
  completedAt?: string;
  startedBy: string;
  createdDate: string;
  modifiedDate: string;
}

export interface InvestigationTemplateItem {
  id: string;
  name: string;
  description: string;
  defaultDateRangeDays: number;
  dataSources: string[];
  iocCategories: string[];
}

export async function createInvestigation(data: {
  clientId: string;
  templateId?: string;
  title: string;
  targetUsers: string[];
  dateRangeStart: string;
  dateRangeEnd: string;
  dataSources?: string[];
  iocCategories?: string[];
  knownAttackerIPs?: string[];
  expectedCountries?: string[];
  description?: string;
  notifyEmail?: string;
}): Promise<{ success: boolean; investigationId: string; clientId: string; clientName: string; status: string; message: string }> {
  const response = await fetchWithAuth('/security/investigations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function getInvestigation(investigationId: string, clientId: string): Promise<{ success: boolean; investigation: InvestigationItem }> {
  const response = await fetchWithAuth(`/security/investigations/${investigationId}?clientId=${encodeURIComponent(clientId)}`);
  return response.json();
}

export async function listInvestigations(clientId?: string): Promise<{
  success: boolean;
  investigations: InvestigationItem[];
  total: number;
  byStatus: { pending: number; collecting: number; analyzing: number; complete: number; failed: number };
}> {
  const params = new URLSearchParams();
  if (clientId) params.set('clientId', clientId);
  const qs = params.toString();
  const response = await fetchWithAuth(`/investigations${qs ? '?' + qs : ''}`);
  return response.json();
}

export async function completeInvestigation(investigationId: string, clientId: string): Promise<{ success: boolean; message: string; previousStatus?: string }> {
  const response = await fetchWithAuth(`/security/investigations/${investigationId}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId }),
  });
  return response.json();
}

export interface InvestigationFindingItem {
  id: string;
  clientId: string;
  clientName: string;
  tenantDomain: string;
  userId?: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  summary: string;
  details: string;
  rawData?: any;
  detectedAt: string;
  status: string;
  source: string;
  collectionId?: string;
}

export async function getInvestigationFindings(investigationId: string, clientId: string): Promise<{
  success: boolean;
  findings: InvestigationFindingItem[];
  bySeverity: { critical: number; warning: number; info: number };
  byStatus: { new: number; resolved: number; false_positive: number };
  overallStatus: 'compromised' | 'at_risk' | 'clean';
  total: number;
}> {
  const response = await fetchWithAuth(`/security/investigations/${investigationId}/findings?clientId=${encodeURIComponent(clientId)}`);
  return response.json();
}

export async function getInvestigationReport(investigationId: string, clientId: string): Promise<{
  success: boolean;
  html: string;
  generatedAt: string;
  investigationId: string;
  clientId: string;
  findingCount: number;
}> {
  const response = await fetchWithAuth(`/security/investigations/${investigationId}/report?clientId=${encodeURIComponent(clientId)}`);
  return response.json();
}

export async function sendInvestigationSummary(
  investigationId: string,
  data: { clientId: string; recipientEmail: string; recipientName?: string }
): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth(`/security/investigations/${investigationId}/email-summary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export interface AttackerContact {
  recipient: string;
  count: number;
  subjects: string[];
  dates: string[];
  senders: string[];
}

export async function getAttackerContacts(
  investigationId: string,
  clientId: string
): Promise<{ success: boolean; contacts: AttackerContact[]; attackerIPCount?: number }> {
  const response = await fetchWithAuth(`/security/investigations/${investigationId}/attacker-contacts?clientId=${encodeURIComponent(clientId)}`);
  return response.json();
}

// --- EXO Collection Settings ---

export interface EXOCollectionSettingsData {
  nightlyEnabled: boolean;
  compromiseTriggerEnabled: boolean;
  nightlyWindowStart: number;
  nightlyWindowEnd: number;
  nightlyTimezone: string;
  modifiedAt?: string;
  modifiedBy?: string;
}

export async function getEXOCollectionSettings(): Promise<{ success: boolean; settings: EXOCollectionSettingsData }> {
  const response = await fetchWithAuth('/security/exo-collection-settings');
  return response.json();
}

export async function saveEXOCollectionSettings(
  settings: Partial<EXOCollectionSettingsData>
): Promise<{ success: boolean; settings: EXOCollectionSettingsData }> {
  const response = await fetchWithAuth('/security/exo-collection-settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
  return response.json();
}

// --- Collection Coverage ---

export interface CollectionCoverageData {
  earliest: string | null;
  latest: string | null;
  lastCollectionAt: string | null;
  hasEXOData: boolean;
  totalCollections: number;
}

export async function getCollectionCoverage(clientId: string): Promise<{ success: boolean } & CollectionCoverageData> {
  const response = await fetchWithAuth(`/security/clients/${clientId}/coverage`);
  return response.json();
}

// --- Seed Collection ---

export async function triggerSeedCollection(clientId: string): Promise<{
  success: boolean;
  message: string;
  userCount?: number;
  exoJobId?: string;
  collectionId?: string;
}> {
  const response = await fetchWithAuth(`/security/clients/${clientId}/seed`, {
    method: 'POST',
  });
  return response.json();
}

// --- Smart Sync (unified sync + forensic collection) ---

export interface SmartSyncResponse {
  success: boolean;
  mode: 'seed' | 'sync';
  userCount: number;
  groupCount: number;
  mailboxCount: number;
  mailboxStatus: 'synced' | 'failed' | 'skipped';
  forensicUsers: number;
  analysis: {
    findingCount: number;
    deltaFindingCount: number;
    alertCreated: boolean;
  };
  exoJobId?: string;
  collectionId: string;
  durationMs?: number;
  error?: string;
}

export async function smartSync(clientId: string): Promise<SmartSyncResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/smart-sync`, {
    method: 'POST',
  });
  return response.json();
}

export async function getInvestigationTemplates(): Promise<{
  success: boolean;
  templates: InvestigationTemplateItem[];
  total: number;
}> {
  const response = await fetchWithAuth('/security/investigation-templates');
  return response.json();
}

// ============================================
// SpamHero Connector Health Check (Email Health)
// ============================================

export interface DnsRecordAction {
  type: 'CNAME' | 'TXT' | 'MX';
  host: string;
  value: string;
  description?: string;
}

export interface SpamHeroCheckDetail {
  status: 'pass' | 'fail' | 'warning' | 'unknown' | 'n/a';
  message: string;
  details?: string;
  dnsRecords?: DnsRecordAction[];
  instructions?: string[];
  spfLookupCount?: number;
}

export interface SpamHeroCheckResult {
  id: string;
  clientId: string;
  clientName: string;
  domain: string;
  usesSpamHero: boolean;
  mxRecords: string[];
  checks: {
    mxRouting: SpamHeroCheckDetail;
    inboundConnector: SpamHeroCheckDetail;
    enhancedFiltering: SpamHeroCheckDetail;
    ipMatch: SpamHeroCheckDetail;
    spf?: SpamHeroCheckDetail;
    dkim?: SpamHeroCheckDetail;
    dmarc?: SpamHeroCheckDetail;
  };
  overallStatus: 'healthy' | 'issues' | 'critical' | 'n/a';
  connectorName?: string;
  connectorIps?: string[];
  checkedAt: string;
  checkedBy: 'scheduled' | 'manual';
  error?: string;
}

export interface SpamHeroHealthSummary {
  total: number;
  healthy: number;
  issues: number;
  critical: number;
  na: number;
  spamHeroClients: number;
}

export interface SpamHeroSettingsData {
  id: string;
  knownGoodIps: string[];
  modifiedAt: string;
  modifiedBy: string;
}

export async function getEmailHealth(): Promise<{
  success: boolean;
  summary: SpamHeroHealthSummary;
  checks: SpamHeroCheckResult[];
}> {
  const response = await fetchWithAuth('/email-health');
  return response.json();
}

export type DkimMigrationStatusValue = 'current' | 'migrated' | 'not-migrated' | 'no-config' | 'no-dkim' | 'unknown';

export interface DkimMigrationClient {
  clientId: string;
  clientName: string;
  domain: string;
  migrationStatus: DkimMigrationStatusValue;
  dkimSigningEnabled?: boolean;
  selector1Actual?: string;
  selector2Actual?: string;
  selector1Expected?: string;
  selector2Expected?: string;
  checkedAt: string;
}

export interface DkimMigrationSummary {
  total: number;
  current: number;
  migrated: number;
  notMigrated: number;
  noConfig: number;
  noDkim: number;
  unknown: number;
}

export async function getDkimMigrationStatus(): Promise<{
  success: boolean;
  summary: DkimMigrationSummary;
  clients: DkimMigrationClient[];
}> {
  const response = await fetchWithAuth('/dkim-migration-status');
  return response.json();
}

export async function getClientEmailHealth(clientId: string): Promise<{
  success: boolean;
  latest: SpamHeroCheckResult | null;
  history: SpamHeroCheckResult[];
}> {
  const response = await fetchWithAuth(`/email-health/${clientId}`);
  return response.json();
}

export async function runEmailHealthCheck(clientId: string): Promise<SpamHeroCheckResult> {
  // Uses fetchDirect to bypass the 45s SWA proxy timeout — runbook + DNS lookups exceed it
  const response = await fetchDirect(`/email-health/${clientId}/check`, { method: 'POST' });
  return response.json();
}

export async function fixEnhancedFiltering(
  clientId: string,
  connectorName: string,
  skipIPs: string[]
): Promise<{
  success: boolean;
  message?: string;
  connectorName?: string;
  efSkipIPs?: string[];
  efEnabled?: boolean;
  error?: string;
  details?: string;
}> {
  // Uses fetchDirect — runbook-backed, exceeds 45s SWA proxy timeout
  const response = await fetchDirect(`/clients/${clientId}/email-health/fix-ef`, {
    method: 'POST',
    body: JSON.stringify({ connectorName, skipIPs }),
  });
  return response.json();
}

export async function getSpamHeroSettings(): Promise<{
  success: boolean;
  settings: SpamHeroSettingsData;
}> {
  const response = await fetchWithAuth('/spamhero/settings');
  return response.json();
}

export async function saveSpamHeroSettings(knownGoodIps: string[]): Promise<{
  success: boolean;
  message: string;
}> {
  const response = await fetchWithAuth('/spamhero/settings', {
    method: 'POST',
    body: JSON.stringify({ knownGoodIps }),
  });
  return response.json();
}

// ============================================
// Client Check Timestamps (for Clients table)
// ============================================

export interface ClientCheckTimestamp {
  lastSync: string | null;
  lastEmailCheck: string | null;
  lastSecurityScan: string | null;
}

export async function getClientCheckTimestamps(): Promise<Record<string, ClientCheckTimestamp>> {
  const response = await fetchWithAuth('/check-timestamps');
  const data = await response.json();
  return data.timestamps || {};
}

// ============================================
// Bulk User Operations
// ============================================

export interface BulkOperationResult {
  userId: string;
  success: boolean;
  error?: string;
  password?: string;
}

export interface BulkOperationResponse {
  success: boolean;
  results: BulkOperationResult[];
  summary: { total: number; succeeded: number; failed: number };
  error?: string;
}

export async function bulkAssignLicense(clientId: string, userIds: string[], skuId: string): Promise<BulkOperationResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/bulk/assign-license`, {
    method: 'POST',
    body: JSON.stringify({ userIds, skuId }),
  });
  return response.json();
}

export async function bulkResetPasswords(clientId: string, userIds: string[], sendEmail?: boolean): Promise<BulkOperationResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/bulk/reset-passwords`, {
    method: 'POST',
    body: JSON.stringify({ userIds, sendEmail }),
  });
  return response.json();
}

export async function bulkToggleUsers(clientId: string, userIds: string[], enabled: boolean): Promise<BulkOperationResponse> {
  const response = await fetchWithAuth(`/clients/${clientId}/bulk/toggle-users`, {
    method: 'POST',
    body: JSON.stringify({ userIds, enabled }),
  });
  return response.json();
}

// ============================================
// Spam Sample Upload Portal
// ============================================

export interface SpamReceivedHop {
  from?: string;
  by?: string;
  timestamp?: string;
  raw: string;
}

export interface SpamAuthResult {
  result: string;
  domain?: string;
}

export interface SpamFileAnalysis {
  fileName: string;
  fileSize: number;
  from: string;
  to: string;
  subject: string;
  date: string;
  spf?: SpamAuthResult;
  dkim?: SpamAuthResult;
  dmarc?: SpamAuthResult;
  authResultsRaw?: string;
  receivedHops: SpamReceivedHop[];
  passedThroughSpamHero: boolean;
  originatingIP?: string;
  parseError?: string;
}

export interface SpamUploadRequestData {
  id: string;
  token: string;
  clientId: string;
  clientName: string;
  contactEmail: string;
  contactName: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  expiresAt: string;
  status: 'pending' | 'uploaded' | 'expired';
  uploadedAt?: string;
  fileCount?: number;
  analysis?: { files: SpamFileAnalysis[] };
  notes?: string;
}

export async function createSpamUploadRequest(data: {
  clientId: string;
  clientName: string;
  contactEmail: string;
  contactName: string;
  expiresInHours?: number;
  notes?: string;
}): Promise<{ success: boolean; token: string; id: string; expiresAt: string }> {
  const response = await fetchWithAuth('/spam-upload-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function sendSpamUploadEmail(data: {
  recipientEmail: string;
  contactName: string;
  clientName: string;
  uploadLink: string;
  expiresAt: string;
}): Promise<{ success: boolean; message?: string }> {
  const response = await fetchWithAuth('/send-spam-upload-email', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function getSpamUploadRequests(clientId?: string): Promise<{ success: boolean; items: SpamUploadRequestData[] }> {
  const params = clientId ? `?clientId=${clientId}` : '';
  const response = await fetchWithAuth(`/spam-upload-requests${params}`);
  return response.json();
}

export async function getSpamUploadDetail(id: string): Promise<{ success: boolean; request: SpamUploadRequestData }> {
  const response = await fetchWithAuth(`/spam-upload-requests/${id}`);
  return response.json();
}

// =============================================================================
// DMARC Reports
// =============================================================================

export interface DmarcConfig {
  id: string;
  clientId: string;
  policyDomain: string;
  currentPolicy: string | null;
  ruaAddresses: string[];
  hasExternalRuaAuth: boolean;
  lastReportAt: string | null;
  firstReportAt: string | null;
  totalReports: number;
  onboardedAt: string;
  updatedAt: string;
}

export interface DmarcRollup {
  id: string;
  clientId: string;
  policyDomain: string;
  date: string;
  totalMessages: number;
  passingMessages: number;
  failingMessages: number;
  spfPassRate: number;
  dkimPassRate: number;
  bySourceIp: Array<{
    ip: string;
    messages: number;
    passing: number;
    failing: number;
    headerFroms: string[];
  }>;
  reportCount: number;
  updatedAt: string;
}

export interface DmarcReportSummary {
  id: string;
  reporter: string;
  policyDomain: string;
  dateRangeBegin: number;
  dateRangeEnd: number;
  totalMessages: number;
  passingMessages: number;
  failingMessages: number;
  recordCount: number;
  ingestedAt: string;
}

export async function getDmarcReports(clientId: string, days = 30): Promise<{
  config: DmarcConfig | null;
  rollups: DmarcRollup[];
  reports: DmarcReportSummary[];
}> {
  const response = await fetchWithAuth(`/dmarc/client/${clientId}?days=${days}`);
  return response.json();
}

// ============================================================================
// Compliance posture (HIPAA / SOC2 / PCI / GDPR)
// ============================================================================

export type ComplianceFramework = 'hipaa' | 'soc2' | 'pci' | 'gdpr';
export type ComplianceSeverity = 'must-fix' | 'should-fix' | 'nice-to-have';

export interface ComplianceGap {
  id: string;
  title: string;
  detail: string;
  remediation: string;
  severity: ComplianceSeverity;
}

export interface ComplianceEvaluation {
  framework: ComplianceFramework;
  score: number;
  gaps: ComplianceGap[];
  passedRuleIds: string[];
  evaluatedAt: string;
}

// The cached posture object — shape mirrors SyncedCompliancePosture on the API.
// Kept as a loose shape here since the UI only reads a few top-line fields;
// full drill-down uses evaluation.gaps.
export interface CompliancePostureSnapshot {
  capturedAt: string;
  mailboxes: unknown[];
  transportRules: unknown[];
  dlpPolicies: unknown[];
  dlpRules: unknown[];
  retentionPolicies: unknown[];
  inboundConnectors: unknown[];
  outboundConnectors: unknown[];
  userAuthMethods: unknown[];
  licenseInventory: { skuPartNumber: string | null; displayName: string; count: number }[];
  securityDefaultsEnabled: boolean;
}

export interface CompliancePostureResponse {
  success: true;
  clientId: string;
  clientName: string;
  framework: ComplianceFramework;
  posture: CompliancePostureSnapshot | null;
  evaluation: ComplianceEvaluation | null;
  isExoStubbed: boolean;
  capturedAt: string | null;
  lastSynced: string | null;
  staleness: 'fresh' | 'stale' | 'very_stale' | 'never_synced';
  needsSync: boolean;
}

export async function getCompliancePosture(
  clientId: string,
  framework: ComplianceFramework = 'hipaa',
): Promise<CompliancePostureResponse> {
  const response = await fetchWithAuth(`/security/compliance/${clientId}/${framework}`);
  return response.json();
}

// ── Cross-client portfolio ──────────────────────────────────────────────────

export interface CompliancePortfolioRow {
  clientId: string;
  clientName: string;
  hasSnapshot: boolean;
  score: number | null;
  mustFix: number;
  shouldFix: number;
  niceToHave: number;
  totalGaps: number;
  isExoStubbed: boolean;
  capturedAt: string | null;
  lastSynced: string | null;
  staleness: 'fresh' | 'stale' | 'very_stale' | 'never_synced';
}

export interface CompliancePortfolioStats {
  framework: ComplianceFramework;
  totalClients: number;
  withSnapshot: number;
  neverSynced: number;
  averageScore: number | null;
  belowThreshold: number;
  exoStubbed: number;
}

export interface CompliancePortfolioResponse {
  success: true;
  framework: ComplianceFramework;
  stats: CompliancePortfolioStats;
  rows: CompliancePortfolioRow[];
  generatedAt: string;
}

export async function getCompliancePortfolio(
  framework: ComplianceFramework = 'hipaa',
): Promise<CompliancePortfolioResponse> {
  const response = await fetchWithAuth(`/security/compliance-portfolio/${framework}`);
  return response.json();
}

export interface TriggerComplianceSyncResponse {
  success: boolean;
  status: 'success' | 'failed' | 'skipped';
  clientId: string;
  clientName: string | null;
  reason?: string;
  durationMs: number;
  error: string | null;
}

/**
 * Trigger an on-demand compliance posture refresh for one client. Runs the
 * same Azure Automation runbook the monthly ScheduledComplianceSync uses,
 * so this can take ~90s (cold start + EXO connect + cmdlets).
 *
 * Uses fetchWithAuth (SWA proxy) — same auth path as every other operation.
 * The SWA proxy times out at 45s, but the Function keeps running server-side
 * and writes the result to Cosmos when it finishes; the caller handles the
 * timeout by reloading cached data shortly after (see CompliancePage.refresh).
 */
export async function triggerComplianceSync(clientId: string): Promise<TriggerComplianceSyncResponse> {
  const response = await fetchWithAuth(`/security/compliance/${clientId}/sync`, { method: 'POST' });
  return response.json();
}

/**
 * Fetch the HIPAA compliance report HTML for a client and open it in a new
 * browser tab. From the new tab the user can save as PDF via the browser's
 * built-in print dialog (Cmd/Ctrl+P → Save as PDF).
 *
 * Fast (reads cached posture, no runbook) so the SWA proxy path is fine.
 * Passes allowHtml so fetchWithAuth's HTML-redirect guard doesn't misread the
 * report's legitimate text/html body as an auth failure.
 */
export async function openHipaaReport(clientId: string): Promise<void> {
  const response = await fetchWithAuth(`/security/compliance/${clientId}/report/hipaa`, {}, { allowHtml: true });
  const html = await response.text();
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Release the object URL after a reasonable delay so the tab has time to load.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ----------------------------------------------------------------------------
// Pax8 portfolio margin audit
// ----------------------------------------------------------------------------
// Contract mirrored to the OverSite V3 Pax8 connector shape so this page can
// eventually be lifted out and re-parented under a connector. Keep it purely
// derived from what the API returns — no cross-references to Cosmos or other
// client-only state.

export type MarginAuditMsrpFlag = 'AT_MSRP' | 'ABOVE_MSRP' | 'BELOW_MSRP' | 'NO_MSRP';

export interface MarginAuditRow {
  companyId: string;
  companyName: string;
  subscriptionId: string;
  productId: string;
  productName: string;
  vendor: string;
  quantity: number;
  status: string;
  billingTerm: string;
  commitmentTerm: string;
  renewalDate: string;
  unitSellPrice: number;
  unitCost: number;
  unitMsrp: number | null;
  monthlyBill: number;
  monthlyCost: number;
  monthlyMargin: number;
  marginPct: number;
  pctOfMsrp: number | null;
  msrpFlag: MarginAuditMsrpFlag;
  belowCost: boolean;
}

export interface MarginAuditSummary {
  companiesScanned: number;
  subscriptionsInScope: number;
  uniqueProducts: number;
  rowsReturned: number;
  monthlyBill: number;
  monthlyCost: number;
  monthlyMargin: number;
  marginPct: number;
  belowCostLines: number;
  belowMsrpLines: number;
  atMsrpLines: number;
  noMsrpLines: number;
}

export interface MarginAuditResponse {
  generatedAt: string;
  durationMs: number;
  summary: MarginAuditSummary;
  rows: MarginAuditRow[];
}

/**
 * Walk every active Pax8 company + in-scope subscription and return a per-line
 * margin audit (sell / cost / MSRP / margin% / flags). Runs 30-45s across the
 * full book — direct-to-Function-App to avoid the 45s SWA proxy timeout.
 */
export async function getMarginAudit(opts?: {
  companyId?: string;
  belowCostOnly?: boolean;
  belowMsrpOnly?: boolean;
  includeInactive?: boolean;
}): Promise<MarginAuditResponse> {
  const params = new URLSearchParams({ format: 'json' });
  if (opts?.companyId) params.set('companyId', opts.companyId);
  if (opts?.belowCostOnly) params.set('belowCostOnly', 'true');
  if (opts?.belowMsrpOnly) params.set('belowMsrpOnly', 'true');
  if (opts?.includeInactive) params.set('includeInactive', 'true');
  const response = await fetchDirect(`/pax8/margin-audit?${params.toString()}`);
  return response.json() as Promise<MarginAuditResponse>;
}
